import { Component, Property, PhysXComponent, ForceMode, Mesh, MeshComponent, Physics, CollisionComponent, TextComponent } from '@wonderlandengine/api';
import { HowlerAudioSource, CursorTarget } from '@wonderlandengine/components';
import { vec3, vec4, quat } from 'gl-matrix';
import { ProgressionSystem } from './progression-system.js';
import { KinematicBullet } from './kinematic-bullet.js';
import { BoostPurchaseHandler } from './boost-purchase-handler.js';
import { AnalyticsManager } from './analytics-manager.js';

export class FPSMovement extends Component {
    static TypeName = 'fps_movement';

    // QUEST 2 OPTIMIZATION: Hoist icon colors to class level (avoids per-call array allocation)
    static _ICON_HIGHLIGHT = new Float32Array([0, 1, 1, 1]);
    static _ICON_AVAILABLE = new Float32Array([0.2, 0.6, 1, 1]);
    static _ICON_INACTIVE = new Float32Array([0.1, 0.1, 0.1, 0.5]);
    static _ICON_WEAPONS = ['default', 'rifle', 'shotgun', 'sniper'];

    static Properties = {
        shop: Property.object(),
        network: Property.object(),
        playerRig: Property.object(),
        botPool: Property.object(),
        head: Property.object(),
        leftController: Property.object(),
        rightController: Property.object(),
        feet: Property.object(),
        speed: Property.float(6.0),
        staminaBar: Property.object(),
        airControl: Property.float(0.42),
        jumpForce: Property.float(16),
        rotationSpeed: Property.float(3.0),
        maxSlopeAngle: Property.float(50.0),
        coyoteTime: Property.float(0.15),
        jumpBufferTime: Property.float(0.15),
        maxJumpCount: Property.int(2), // Allow double jump (1 ground + 1 air)
        maxEndurance: Property.float(9.0),
        enduranceRecoveryRate: Property.float(1),
        enduranceDrainRate: Property.float(0.84),
        leftGun: Property.object(),
        rightGun: Property.object(),
        rifleGun: Property.object(),
        groundCheckDistance: Property.float(10),
        leftGunMuzzleFlash: Property.object(),
        rightGunMuzzleFlash: Property.object(),
        rifleGunMuzzleFlash: Property.object(),
        shotgunGunMuzzleFlash: Property.object(),
        leftGunOrigin: Property.object(),
        rightGunOrigin: Property.object(),
        leftGunTarget: Property.object(),
        rightGunTarget: Property.object(),
        rifleGunOrigin: Property.object(),
        rifleGunTarget: Property.object(),
        crosshairRifleGun: Property.object(),
        laserRifleGun: Property.object(),
        shotgunGun: Property.object(),
        shotgunGunOrigin: Property.object(),
        shotgunGunTarget: Property.object(),
        shotgunLaser: Property.object(),
        shotgunCrosshair: Property.object(),
        bulletPoolLeftGun: Property.object(),
        bulletPoolRightGun: Property.object(),
        laserLeftGun: Property.object(),
        laserRightGun: Property.object(),
        crosshairLeftGun: Property.object(),
        crosshairRightGun: Property.object(),
        gameInfoTextObject: Property.object(),
        unlockSoundObject: Property.object(),
        akimboIcon: Property.object(),
        rifleIcon: Property.object(),
        shotgunIcon: Property.object(),
        MUZZLE_FLASH_DURATION: Property.float(0.1), // seconds
        // SNIPER PROPERTIES
        sniperGun: Property.object(),
        sniperGunMuzzleFlash: Property.object(),
        sniperGunOrigin: Property.object(),
        sniperGunTarget: Property.object(),
        sniperLaser: Property.object(),
        crosshairSniperGun: Property.object(),
        sniperIcon: Property.object(),
        debugLaserToggle: Property.bool(false), // Debug property to toggle laser colors and active state
        debugAllWeapons: Property.bool(false), // Debug: unlock all weapons regardless of progression
    };

    // PERF: Hoisted to class level — shared across all instances, zero per-instance allocation
    static ADS_CONFIGS = {
        'default': { fov: 75, sens: 0.70, moveScale: 0.7, gunFwd: 0.3, gunDown: 0.18, gunRight: 0.0 },
        'rifle': { fov: 65, sens: 0.55, moveScale: 0.6, gunFwd: 0.3, gunDown: 0.18, gunRight: 0.0 },
        'shotgun': { fov: 78, sens: 0.75, moveScale: 0.7, gunFwd: 0.3, gunDown: 0.18, gunRight: 0.0 },
        'sniper': { fov: 40, sens: 0.35, moveScale: 0.5, gunFwd: 0.35, gunDown: 0.18, gunRight: 0.0 },
    };
    static SWAY_MULTIPLIERS = { pistol: 0.02, rifle: 0.015, shotgun: 0.01, sniper: 0.005 };

    init() {
        this._moveDir = vec3.create();
        this._velocity = vec3.create();
        this._up = vec3.fromValues(0, 1, 0);
        this._origin = vec3.create();
        this._groundNormal = vec3.fromValues(0, 1, 0);
        this._sprintTime = 0;       // Tracks duration for exponential drain
        this._depletionTimer = 0;   // The 3s penalty clock
        this._isDraining = false;   // Strict state tracking
        this._sprintExhausted = false;
        // In init()
        this._playerRigRot = quat.create(); // Correctly creates [0, 0, 0, 1]
        this._headForward = vec3.create();
        this._headRight = vec3.create();
        this.pos = new Float32Array(3);
        this.pos2 = new Float32Array(3);
        this.directionLeftBullet = new Float32Array(3);
        this.directionRightBullet = new Float32Array(3);
        this._tempVelocity = vec3.create();
        this._tempDesiredMove = vec3.create();
        this._tempDirection = vec3.create();
        this._downVector = vec3.fromValues(0, -1, 0);
        this._tempOrigin = vec3.create();
        this._bulletDirection = vec3.create();
        this._raycastDirection = vec3.create();
        this._tempRigRot = quat.create();
        this._tempDeltaRot = quat.create();
        this._zeroScale = new Float32Array(3); // [0,0,0] reusable
        this._tempMuzzleScale = new Float32Array(3);
        this.directions = [
            vec3.fromValues(1, 0, 0),
            vec3.fromValues(-1, 0, 0),
            vec3.fromValues(0, 0, 1),
            vec3.fromValues(0, 0, -1),
        ];
        this._keys = new Set();
        this._validPickupNames = new Set(['RiflePickup', 'ShotgunPickup', 'SniperPickup', 'AkimboPickup', 'JumpPad']);
        // PERF: Pre-cache PhysXComponent on every bullet in both pools
        this._kinBulletLeft = [];   // kinematic-bullet refs for bulletPoolLeftGun
        this._kinBulletRight = [];   // kinematic-bullet refs for bulletPoolRightGun
        this._meshStates = { rifle: false, shotgun: false, sniper: false, leftGun: false, rightGun: false };
        this._rifleMeshes = null;
        this._shotgunMeshes = null;
        this._sniperMeshes = null;
        this._leftGunMeshes = null;
        this._rightGunMeshes = null;
        this._updateTimers = { ground: 0, pickups: 0, ui: 0 };
        this._updateIntervals = { ground: 1 / 10, pickups: 1 / 3, ui: 1 / 3 };
        this._lobbySpawnPos = vec3.create();
        this._lobbySpawnRot = quat.create();
        this.isAirborne = false;
        this.staminaBarScale = vec3.fromValues(0.5, 0.005, 0.5);
        if (this.staminaBar) this.staminaBar.setScalingLocal(this.staminaBarScale);
        // === Pre-allocate weapon position/rotation arrays (eliminates lazy allocs in getters) ===
        this._weaponPos = new Float32Array(3);
        this._weaponRot = new Float32Array([0, 0, 0, 1]);
        this._weaponPosLeft = new Float32Array(3);
        this._weaponRotLeft = new Float32Array([0, 0, 0, 1]);
        this._viewYawRot = new Float32Array([0, 0, 0, 1]);
        this._rigRotTemp = new Float32Array(4);

        // === Pre-allocate quats for gun reset (eliminates per-call quat.create in _resetGunLocalsForXR) ===
        this._akimboRot = quat.create();
        quat.fromEuler(this._akimboRot, -120, 0, 180);
        this._rifleRot = quat.create();
        quat.fromEuler(this._rifleRot, -60, 0, 0);
        // Cache editor scale for guns so _resetGunLocalsForXR can restore after desktop mode
        this._gunEditorScalesCached = false;

        // === Pre-allocate desktop input vectors (moved from _initInputListeners) ===
        this._headPitchQuat = quat.create();
        this._headYawQuat = quat.create();
        this._adsGunPos = new Float32Array(3);
        this._akimboDesktopForward = new Float32Array(3);
        this._akimboDesktopRight = new Float32Array(3);
        this._akimboDesktopPos = new Float32Array(3);

        // === Static configs — reference class-level constants (no per-instance allocation) ===
        this._adsConfigs = FPSMovement.ADS_CONFIGS;
        this._swayMultipliers = FPSMovement.SWAY_MULTIPLIERS;

        // === Muzzle flash scale holders (populated once in start() via Float32Array.set) ===
        this._leftGunMuzzleFlashScale = new Float32Array(3);
        this._rightGunMuzzleFlashScale = new Float32Array(3);
        this._rifleGunMuzzleFlashScale = new Float32Array(3);
        this._shotgunGunMuzzleFlashScale = new Float32Array(3);
        this._sniperGunMuzzleFlashScale = new Float32Array(3);
        this._muzzleFlashScalesCached = false;

        // === Pre-allocated objects for update loop (eliminates GC pauses) ===
        this._inputState = { x: 0, y: 0, rot: 0, jumpPressed: false, sprint: false, switchAimAssist: false, leftTrigger: null, rightTrigger: null, pickupPressed: false };
        this._desktopMouseTrigger = { pressed: false };
        this._tempStaminaScale = new Float32Array(3);
        this._pickupTexts = {
            'E': { empty: 'Press E to Pick Up Weapon', swap: 'Press E to swap' },
            'X': { empty: 'Press X to Pick Up Weapon', swap: 'Press X to swap' }
        };

        // === Desktop mouse / ADS state ===
        this._mouseLeftDown = false;
        this._mouseRightDown = false;
        this._isPointerLocked = false;
        this._mouseSensitivity = 0.002;
        this._headPitch = 0;
        this._desktopYaw = 0;
        this._mouseDeltaX = 0;
        this._mouseDeltaY = 0;
        this._adsBlend = 0;
        this._adsActive = false;
        this._baseFov = 90;
        this._adsViewComponent = null;
        this._adsFovInitialized = false;
        this._adsLerpSpeed = 8.0;
        this._adsOutSpeed = 12.0;
        this._adsLeftGunHidden = false;

        // === dt-based timers replacing setTimeout (eliminates GC pressure from closures) ===
        this._weaponSwitchTimer = 0;
        this._weaponSwitchPendingSlot = -1;
        this._weaponSwitchPendingType = null; // 'select' or 'cycle'
        this._pickupMessageTimer = 0;
        this._pickupMessageName = '';
        this._loadoutResetTimer = 0;
        this._networkInitTimer = 0;
        this._networkInitPending = false;

        // === Arrays that must exist before first _initState call ===
        this._overlappingPickups = [];
        this._weaponSlots = ['default', null];

        // === Gun initial world transforms (populated in start()) ===
        this._initialGunWorlds = {};

        // === Icon color/position caches (pre-allocated Float32Arrays, filled in start()) ===
        this._iconOriginalColors = {
            'default': new Float32Array(4),
            'rifle': new Float32Array(4),
            'shotgun': new Float32Array(4),
            'sniper': new Float32Array(4),
        };
        this._iconSlotPositions = [
            new Float32Array(3),
            new Float32Array(3),
            new Float32Array(3),
            new Float32Array(3),
        ];

        // === Cached laser MeshComponent refs (populated in start(), reused in _initLaserColors) ===
        this._laserMeshCache = [null, null, null, null, null];

        // === Sniper fire timer ===
        this._sniperFireTimer = 0;

        // === Cached component refs (populated in start()) ===
        this._nonVrCamera = null;
    }
    start() {
        // ===== ONE-TIME COMPONENT CACHING (all scene lookups happen here, never in reset paths) =====
        this.playerRig.getPositionWorld(this._lobbySpawnPos);
        this.playerRig.getRotationWorld(this._lobbySpawnRot);
        // PhysX on player body
        this._physx = this.object.getComponent(PhysXComponent);
        this.shopBuyButton = this.shop.getComponent(BoostPurchaseHandler);
        // UI elements: cache MeshComponent refs once (stable across resets)
        this.akimboIcon.mesh = this.akimboIcon.getComponent(MeshComponent);
        this.rifleIcon.mesh = this.rifleIcon.getComponent(MeshComponent);
        this.shotgunIcon.mesh = this.shotgunIcon.getComponent(MeshComponent);
        this.sniperIcon.mesh = this.sniperIcon.getComponent(MeshComponent);
        this.unlockSound = this.unlockSoundObject.getComponent(HowlerAudioSource);
        this.gameInfoText = this.gameInfoTextObject.getComponent(TextComponent);
        this.playerCollision = this.playerRig.getComponent(CollisionComponent);

        // Weapon mesh component arrays (cached for _setMeshesActive — no per-switch getComponents)
        this._rifleMeshes = this.rifleGun.getComponents(MeshComponent);
        this._shotgunMeshes = this.shotgunGun.getComponents(MeshComponent);
        this._sniperMeshes = this.sniperGun.getComponents(MeshComponent);
        this._leftGunMeshes = this.leftGun.getComponents(MeshComponent);
        this._rightGunMeshes = this.rightGun.getComponents(MeshComponent);

        if (this.bulletPoolLeftGun) {
            const kids = this.bulletPoolLeftGun.children;
            for (let i = 0; i < kids.length; i++) {
                if (!kids[i]._kinBulletCache) {
                    kids[i]._kinBulletCache = kids[i].getComponent(KinematicBullet);
                }
                this._kinBulletLeft.push(kids[i]._kinBulletCache);
            }
        }
        if (this.bulletPoolRightGun) {
            const kids = this.bulletPoolRightGun.children;
            for (let i = 0; i < kids.length; i++) {
                if (!kids[i]._kinBulletCache) {
                    kids[i]._kinBulletCache = kids[i].getComponent(KinematicBullet);
                }
                this._kinBulletRight.push(kids[i]._kinBulletCache);
            }
        }

        // Muzzle flash original scales (one-time cache using Float32Array.set — no .slice())
        this._leftGunMuzzleFlashScale.set(this.leftGunMuzzleFlash.getScalingLocal());
        this._rightGunMuzzleFlashScale.set(this.rightGunMuzzleFlash.getScalingLocal());
        this._rifleGunMuzzleFlashScale.set(this.rifleGunMuzzleFlash.getScalingLocal());
        this._shotgunGunMuzzleFlashScale.set(this.shotgunGunMuzzleFlash.getScalingLocal());
        this._sniperGunMuzzleFlashScale.set(this.sniperGunMuzzleFlash.getScalingLocal());
        this._muzzleFlashScalesCached = true;

        // Find NonVrCamera as sibling of head (both are children of playerRig/Rig)
        if (this.playerRig) {
            const children = this.playerRig.children;
            for (let i = 0; i < children.length; i++) {
                if (children[i].name === 'NonVrCamera') {
                    this._nonVrCamera = children[i];
                    break;
                }
            }
        }

        // Cache laser MeshComponent refs once (avoids getComponent in _initLaserColors/_cycleDebugLaserState)
        this._laserObjects = [this.laserLeftGun, this.laserRightGun, this.laserRifleGun, this.shotgunLaser, this.sniperLaser];
        for (let i = 0; i < this._laserObjects.length; i++) {
            this._laserMeshCache[i] = this._laserObjects[i] ? this._laserObjects[i].getComponent(MeshComponent) : null;
        }

        // Spawn position (one-time)
        this.spawnPos = new Float32Array(3);
        this.object.getPositionWorld(this.spawnPos);

        // Pre-compute max slope cosine
        this._maxSlopeCos = Math.cos(this.maxSlopeAngle * Math.PI / 180);

        // ===== ONE-TIME AUDIO & WEAPON COMPONENT CACHING =====
        // Cache stable (non-controller-dependent) audio and weapon components once.
        // Controller-dependent refs (leftGunSound, rightGunSound, input) are re-looked-up
        // in _initAudio()/_initAudioDesktop() since XR input sources change per session.
        this.rifleShotSound = this.rifleGun.getComponent(HowlerAudioSource);
        this.shotgunShotSound = this.shotgunGun.getComponent(HowlerAudioSource);
        this._shotgunRifleOrient = this.shotgunGun?.getComponent?.('rifle-orient') || null;
        this._rifleRifleOrient = this.rifleGun?.getComponent?.('rifle-orient') || null;
        this._sniperRifleOrient = this.sniperGun?.getComponent?.('rifle-orient') || null;
        this._sniperShotSound = this.sniperGun?.getComponent?.(HowlerAudioSource) || null;
        this._stableAudioCached = true;

        // ===== INITIAL STATE RESET =====
        // Ensure all lasers are off at very start
        if (this.laserLeftGun) this.laserLeftGun.active = false;
        if (this.laserRightGun) this.laserRightGun.active = false;
        if (this.laserRifleGun) this.laserRifleGun.active = false;
        if (this.shotgunLaser) this.shotgunLaser.active = false;
        if (this.sniperLaser) this.sniperLaser.active = false;

        this._initState();
        this._initIconColors();
        this._initMuzzleFlash();
        this._initInputListeners();
        this._initNetwork();
        this._initLaserColors();
        this.debugToggleLaserColors(); // Debug: toggle laser colors if enabled
        this._updateIconHighlights();
        // QUEST 2 OPTIMIZATION: Cache crosshair DOM element once (avoids per-frame getElementById)
        this._crosshairEl = document.getElementById('desktop-crosshair');
        // Add a flag to track controller-dependent initialization
        this._controllersInitialized = false;
        if (this.engine && this.engine.onXRSessionEnd) {
            this._boundOnXRSessionEnd = this.onXRSessionEnd.bind(this);
            this.engine.onXRSessionEnd.add(this._boundOnXRSessionEnd);
        }
        if (this.engine && this.engine.onXRSessionStart) {
            this._boundOnXRSessionStart = this.onXRSessionStart.bind(this);
            this.engine.onXRSessionStart.add(this._boundOnXRSessionStart);
        }
        // Listen for desktop session start (re-enter) to restore weapon visibility
        this._boundOnDesktopSessionStart = this.onDesktopSessionStart.bind(this);
        window.addEventListener('desktop-session-start', this._boundOnDesktopSessionStart);
        // Listen for desktop session end (EXIT button)
        this._boundOnDesktopSessionEnd = this.onDesktopSessionEnd.bind(this);
        window.addEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
        // Mobile uses the same flat-screen pipeline; reuse desktop handlers.
        window.addEventListener('mobile-session-start', this._boundOnDesktopSessionStart);
        window.addEventListener('mobile-session-end', this._boundOnDesktopSessionEnd);
        // Expose instance so the touch-control HUD can drive _keys / mouse state directly
        window._fpsMovement = this;

        // Cache initial world transforms of all guns (Float32Array copy — no .slice() allocation)
        const guns = { leftGun: this.leftGun, rightGun: this.rightGun, rifleGun: this.rifleGun, shotgunGun: this.shotgunGun, sniperGun: this.sniperGun };
        for (const [key, gun] of Object.entries(guns)) {
            if (gun) {
                this._initialGunWorlds[key] = {
                    pos: new Float32Array(gun.getPositionWorld()),
                    rot: new Float32Array(gun.getRotationWorld()),
                };
            }
        }
    }
    onXRSessionStart() {
        // Reset gun local transforms to editor values for VR hand controllers
        this._resetGunLocalsForXR();
        // Ensure weapon meshes are visible for the current weapon slot
        this._updateWeaponMeshes();
    }

    onXRSessionEnd() {
        this._resetSession();
    }

    /** Called when re-entering desktop mode — restore weapon visibility and icons */
    onDesktopSessionStart() {
        // Ensure weapon meshes reflect current slot state (default akimbo visible)
        this._updateWeaponMeshes();
        this._updateIconHighlights();
        this._updateAimAssistVisibility && this._updateAimAssistVisibility();
        // Re-init audio for desktop in case it was lost
        this._initAudioDesktop();
        this._resetGunLocalsForXR();
        this._controllersInitialized = true;
    }

    /** Called when the EXIT button is clicked — mirrors onXRSessionEnd cleanup */
    onDesktopSessionEnd() {
        // Desktop-specific cleanup before shared reset
        this._isPointerLocked = false;
        if (document.pointerLockElement) {
            document.exitPointerLock();
        }
        this._resetSession();
        // Reset desktop crosshair to default colors (desktop-only)
        this._resetDesktopCrosshair();
    }

    /**
     * Shared session teardown — extracted from onXRSessionEnd / onDesktopSessionEnd.
     * All common reset logic lives here to avoid 80+ lines of duplication.
     */
    _resetSession() {
        // Clear debug laser interval
        if (this._debugLaserInterval) {
            clearInterval(this._debugLaserInterval);
            this._debugLaserInterval = null;
        }

        // Reset desktop mouse state
        this._headPitch = 0;
        this._desktopYaw = 0;
        this._mouseDeltaX = 0;
        this._mouseDeltaY = 0;
        this._mouseLeftDown = false;
        this._mouseRightDown = false;
        // Reset NonVrCamera and EyeLeft rotation to identity
        if (this._nonVrCamera) {
            this._nonVrCamera.setRotationLocal([0, 0, 0, 1]);
        }
        // Reset ADS state
        this._adsBlend = 0;
        this._adsActive = false;
        this._adsLeftGunHidden = false;
        window._adsBlend = 0;
        window._adsWeapon = 'default';
        if (this._adsViewComponent && this._adsFovInitialized) {
            this._adsViewComponent.fov = this._baseFov;
        }
        const chEl = this._crosshairEl;
        if (chEl) {
            chEl.classList.remove('ads', 'ads-sniper');
            chEl.style.display = 'none';
        }
        if (this.head) {
            this.head.setRotationLocal([0, 0, 0, 1]);
        }
        // Clear flags so they don't block next session
        this._loadoutSetupInProgress = false;
        this._controllersInitialized = false;
        // Clear respawning state so update() is not blocked after session restart
        this.isRespawning = false;
        // Pre-kinematic: prevent _initState() from reactivating physics at the
        // arena position.  Setting kinematic BEFORE the reset guarantees no
        // physics step runs at the old location.
        // Reset all player state to initial values
        this._initState();
        this._initIconColors();
        this._initMuzzleFlash();
        this._initLaserColors();
        this._updateIconHighlights();
        this._updateAimAssistVisibility && this._updateAimAssistVisibility();
        // Teleport to lobby (handles kinematic, velocity zeroing, and delayed restore)
        this.teleportToLobby();

        // Hide all muzzle flashes
        this._hideAllMuzzleFlashes && this._hideAllMuzzleFlashes();
        // Reset UI text if available
        if (this.gameInfoText) {
            this.gameInfoText.text = '';
            this.gameInfoText.active = false;
        }
        // Restore all gun world transforms to their initial state
        if (this._initialGunWorlds) {
            const guns = { leftGun: this.leftGun, rightGun: this.rightGun, rifleGun: this.rifleGun, shotgunGun: this.shotgunGun, sniperGun: this.sniperGun };
            for (const [key, gun] of Object.entries(guns)) {
                const saved = this._initialGunWorlds[key];
                if (gun && saved) {
                    gun.setPositionWorld(saved.pos);
                    gun.setRotationWorld(saved.rot);
                }
            }
        }
        this._resetGunLocalsForXR();
    }

    _initState() {
        this.currentWeapon = 'default';
        // deactivate all icons
        this.canUseJumpPad = true;
        this.rifleIcon.active = false;
        this.shotgunIcon.active = false;
        this._wallStuck = false;
        this._jumpCount = 0;
        this._coyoteTimer = 0;
        this._jumpBufferTimer = 0;
        this.rifleBulletIndex = 0;
        this.sniperBulletIndex = 0;
        this._wasJumpPressed = false;
        this._onGround = false;
        this._aimAssistState = 0;
        this._aimAssistManuallySet = false; // Track if player has manually changed aim assist
        this._weaponSwitchToggle = false;
        this._aimAssistToggle = false;
        this._isSwitchingWeapon = false;
        this.fireLeftGun = false;
        this.fireRightGun = false;
        this.fireRifle = false;
        this.fireShotgun = false;
        this._shotgunTriggerPressed = false;
        this._sniperTriggerPressed = false;
        this._shotgunFireTimer = 0; // dt-based cooldown accumulator (seconds)
        this._shotgunCurrentPool = 0;
        this._shotgunPoolIndex = 0;
        // PERF: dt-based fire cooldown timers (replace setTimeout closures)
        this._leftGunFireTimer = 0;
        this._rightGunFireTimer = 0;
        this._rifleFireTimer = 0;
        this.indexLeftGunPool = 0;
        this.indexRightGunPool = 0;
        this._currentEndurance = this.maxEndurance;
        this._sprintExhausted = false;
        this._weaponSlots = ['default', null];
        this._currentSlotIndex = 0;
        this._overlappingPickups = [];
        this.akimboIcon.active = true;
        this.rifleIcon.active = false;
        this.shotgunIcon.active = false;
        this.sniperIcon.active = false;
        this._prevEPressed = false;
        this._jumpPadCooldown = 0;
        this.isRespawning = false;
        this._loadoutSetupInProgress = false;
        // Performance counters
        this._groundCheckCounter = 0;
        this._pickupCheckCounter = 0;
        this._muzzleFlashIdleTimer = 0;
        this._lastGroundCheck = 0;
        this._cachedGroundHit = null;
        this._frameTime = 0;
        this._lastJumpTime = 0;
        this._groundGraceDelay = 500;

        // Reset pre-allocated vectors to zero/identity (no reallocation)
        vec3.zero(this._moveDir);
        vec3.zero(this._velocity);
        vec3.set(this._groundNormal, 0, 1, 0);
        vec3.zero(this._headForward);
        vec3.zero(this._headRight);
        this.pos[0] = this.pos[1] = this.pos[2] = 0;
        this.pos2[0] = this.pos2[1] = this.pos2[2] = 0;
        this.directionLeftBullet[0] = this.directionLeftBullet[1] = this.directionLeftBullet[2] = 0;
        this.directionRightBullet[0] = this.directionRightBullet[1] = this.directionRightBullet[2] = 0;
        this._keys.clear();
        this._meshStates.rifle = false;
        this._meshStates.shotgun = false;
        this._meshStates.sniper = false;
        this._meshStates.leftGun = false;
        this._meshStates.rightGun = false;
        this._updateTimers.ground = 0;
        this._updateTimers.pickups = 0;
        this._updateTimers.ui = 0;

        // dt-based timer resets
        this._weaponSwitchTimer = 0;
        this._weaponSwitchPendingSlot = -1;
        this._weaponSwitchPendingType = null;
        this._pickupMessageTimer = 0;
        this._pickupMessageName = '';

        // Activate PhysX (cached ref from start())
        this._physx.active = true;
        // Activate icon meshes (cached refs from start())
        if (this.akimboIcon.mesh) this.akimboIcon.mesh.active = true;
        if (this.rifleIcon.mesh) this.rifleIcon.mesh.active = true;
        if (this.shotgunIcon.mesh) this.shotgunIcon.mesh.active = true;
        if (this.sniperIcon.mesh) this.sniperIcon.mesh.active = true;
    }
    getCurrentWeaponId() {
        return this._weaponSlots[this._currentSlotIndex];
    }

    getCurrentWeaponPosition() {
        // Desktop: compute a third-person "held weapon" position for network sync.
        // Viewmodel positions (0.3m from camera) end up inside the remote body mesh.
        if (!this._isXRActive()) {
            return this._getDesktopWeaponNetworkPos(this._weaponPos, false);
        }
        const weapon = this._getCurrentWeaponObject();
        this._weaponPos[0] = 0; this._weaponPos[1] = 0; this._weaponPos[2] = 0;
        weapon?.getPositionWorld(this._weaponPos);
        return this._weaponPos;
    }

    getCurrentWeaponRotation() {
        const weapon = this._getCurrentWeaponObject();
        this._weaponRot[0] = 0; this._weaponRot[1] = 0; this._weaponRot[2] = 0; this._weaponRot[3] = 1;
        weapon?.getRotationWorld(this._weaponRot);
        return this._weaponRot;
    }

    /** Return Y-axis-only (yaw) world rotation of the head for network sync */
    getViewYawRotation() {
        // Desktop: combine rig world Y rotation with desktop yaw
        if (!this._isXRActive() && this._desktopYaw !== undefined) {
            this.playerRig.getRotationWorld(this._rigRotTemp);
            // Extract rig's Y-axis yaw from its world rotation
            const rigHalfYaw = Math.atan2(this._rigRotTemp[1], this._rigRotTemp[3]);
            const totalHalfYaw = rigHalfYaw + this._desktopYaw * 0.5;
            this._viewYawRot[0] = 0;
            this._viewYawRot[1] = Math.sin(totalHalfYaw);
            this._viewYawRot[2] = 0;
            this._viewYawRot[3] = Math.cos(totalHalfYaw);
            return this._viewYawRot;
        }

        // VR: extract Y-only from head world rotation
        const h = this.head || this._nonVrCamera;
        if (!h) return this._viewYawRot;
        h.getRotationWorld(this._viewYawRot);
        this._viewYawRot[0] = 0;
        this._viewYawRot[2] = 0;
        const len = Math.sqrt(this._viewYawRot[1] * this._viewYawRot[1] + this._viewYawRot[3] * this._viewYawRot[3]);
        if (len > 0.0001) {
            this._viewYawRot[1] /= len;
            this._viewYawRot[3] /= len;
        } else {
            this._viewYawRot[1] = 0;
            this._viewYawRot[3] = 1;
        }
        return this._viewYawRot;
    }

    getCurrentWeaponPositionLeft() {
        const weaponId = this.getCurrentWeaponId();
        if (weaponId !== 'default') return null;
        if (!this._isXRActive()) {
            return this._getDesktopWeaponNetworkPos(this._weaponPosLeft, true);
        }
        this._weaponPosLeft[0] = 0; this._weaponPosLeft[1] = 0; this._weaponPosLeft[2] = 0;
        this.leftGun?.getPositionWorld(this._weaponPosLeft);
        return this._weaponPosLeft;
    }

    getCurrentWeaponRotationLeft() {
        const weaponId = this.getCurrentWeaponId();
        if (weaponId !== 'default') return null;
        this._weaponRotLeft[0] = 0; this._weaponRotLeft[1] = 0; this._weaponRotLeft[2] = 0; this._weaponRotLeft[3] = 1;
        this.leftGun?.getRotationWorld(this._weaponRotLeft);
        return this._weaponRotLeft;
    }

    /**
     * Desktop mode: compute a visible third-person hold position for network sync.
     * In VR, hand controller positions look natural from third-person. In desktop,
     * viewmodel positions (0.3m from camera) clip inside the remote body mesh.
     * This places the weapon at chest height, extended forward — clearly visible.
     */
    _getDesktopWeaponNetworkPos(outVec, isLeft) {
        // Start from body (feet) world position
        this.object.getPositionWorld(outVec);
        const cam = this._nonVrCamera || this.head;
        if (cam) {
            cam.getForwardWorld(this._akimboDesktopForward);
            cam.getRightWorld(this._akimboDesktopRight);
            const side = isLeft ? -0.20 : 0.20;
            // 0.7m forward, 1.5m up (mid-chest on remote robot mesh), ±0.20m sideways
            outVec[0] += this._akimboDesktopForward[0] * 0.7 + this._akimboDesktopRight[0] * side;
            outVec[1] += 1.5;
            outVec[2] += this._akimboDesktopForward[2] * 0.7 + this._akimboDesktopRight[2] * side;
        }
        return outVec;
    }

    _getCurrentWeaponObject() {
        const id = this.getCurrentWeaponId();
        switch (id) {
            case 'default': return this.rightGun;
            case 'rifle': return this.rifleGun;
            case 'shotgun': return this.shotgunGun;
            case 'sniper': return this.sniperGun;
            default: return null;
        }
    }

    _initIconColors() {
        const icons = [
            { key: 'akimboIcon', name: 'default' },
            { key: 'rifleIcon', name: 'rifle' },
            { key: 'shotgunIcon', name: 'shotgun' },
            { key: 'sniperIcon', name: 'sniper' }
        ];
        for (let i = 0; i < icons.length; ++i) {
            const { key, name } = icons[i];
            const icon = this[key];
            // PERF: Use cached MeshComponent from start() instead of per-reset getComponent WASM call
            const mesh = icon?.mesh;
            if (mesh && mesh.material && mesh.material.diffuseColor) {
                // PERF: .set() into pre-allocated Float32Array instead of .slice()
                this._iconOriginalColors[name].set(mesh.material.diffuseColor);
            }
            // Store original positions for slot alignment
            if (icon && icon.getPositionLocal) {
                // PERF: .set() into pre-allocated Float32Array instead of .slice()
                this._iconSlotPositions[i].set(icon.getPositionLocal());
            }
        }
    }

    _setIconColor(icon, color) {
        // PERF: Use cached MeshComponent from start() instead of per-call getComponent WASM call
        const mesh = icon?.mesh;
        if (mesh && mesh.material) {
            if (color && color.length === 4) {
                //mesh.material.setDiffuseColor(color);
                mesh.material.setEmissiveColor(color);
            }
        }
    }

    _updateIconHighlights() {
        // Get currently active weapon
        const activeWeapon = this._weaponSlots[this._currentSlotIndex];
        const icons = [this.akimboIcon, this.rifleIcon, this.shotgunIcon, this.sniperIcon];
        const names = FPSMovement._ICON_WEAPONS;

        for (let i = 0; i < 4; i++) {
            const icon = icons[i];
            if (!icon) continue;

            const weapon = names[i];
            const isActive = activeWeapon === weapon;
            const isEquipped = this._weaponSlots.includes(weapon);

            icon.active = true;

            if (isActive) {
                this._setIconColor(icon, FPSMovement._ICON_HIGHLIGHT);
            } else if (isEquipped) {
                this._setIconColor(icon, FPSMovement._ICON_AVAILABLE);
            } else {
                this._setIconColor(icon, FPSMovement._ICON_INACTIVE);
            }
        }
    }

    _getIconForWeapon(weapon) {
        switch (weapon) {
            case 'default': return this.akimboIcon;
            case 'rifle': return this.rifleIcon;
            case 'shotgun': return this.shotgunIcon;
            case 'sniper': return this.sniperIcon;
            default: return null;
        }
    }

    _initMuzzleFlash() {
        this.laserLeftGun.active = false;
        this.laserRightGun.active = false;
        this.crosshairLeftGun.active = false;
        this.crosshairRightGun.active = false;
        this.laserRifleGun.active = false;
        this.crosshairRifleGun.active = false;
        this.shotgunLaser.active = false;
        this.shotgunCrosshair.active = false;
        // PERFORMANCE: Cache mesh components on first use to avoid repeated getComponents() calls
        if (!this._rifleMeshes) this._rifleMeshes = this.rifleGun.getComponents(MeshComponent);
        if (!this._shotgunMeshes) this._shotgunMeshes = this.shotgunGun.getComponents(MeshComponent);
        if (!this._sniperMeshes) this._sniperMeshes = this.sniperGun.getComponents(MeshComponent);

        // PERF: for-loop instead of .forEach() — avoids closure allocation on Quest 2
        for (let i = 0; i < this._rifleMeshes.length; i++) this._rifleMeshes[i].active = false;
        for (let i = 0; i < this._shotgunMeshes.length; i++) this._shotgunMeshes[i].active = false;
        for (let i = 0; i < this._sniperMeshes.length; i++) this._sniperMeshes[i].active = false;
        // Only cache original muzzle flash scales on first call (start()).
        // On subsequent calls (session end), scales are already [0,0,0] so
        // re-caching would destroy the original values and make flashes
        // permanently invisible in the next session.
        if (!this._muzzleFlashScalesCached) {
            this._leftGunMuzzleFlashScale = this.leftGunMuzzleFlash.getScalingLocal().slice();
            this._rightGunMuzzleFlashScale = this.rightGunMuzzleFlash.getScalingLocal().slice();
            this._rifleGunMuzzleFlashScale = this.rifleGunMuzzleFlash.getScalingLocal().slice();
            this._shotgunGunMuzzleFlashScale = this.shotgunGunMuzzleFlash.getScalingLocal().slice();
            this._sniperGunMuzzleFlashScale = this.sniperGunMuzzleFlash.getScalingLocal().slice();
            this._muzzleFlashScalesCached = true;
        }
        // PERF: Reuse pre-allocated zero scale instead of [0,0,0] array literals
        this.leftGunMuzzleFlash.setScalingLocal(this._zeroScale);
        this.rightGunMuzzleFlash.setScalingLocal(this._zeroScale);
        this.rifleGunMuzzleFlash.setScalingLocal(this._zeroScale);
        this.shotgunGunMuzzleFlash.setScalingLocal(this._zeroScale);
        this.sniperGunMuzzleFlash.setScalingLocal(this._zeroScale);
        this.leftGunMuzzleFlashIsActive = false;
        this.rightGunMuzzleFlashIsActive = false;
        this.rifleGunMuzzleFlashIsActive = false;
        this.shotgunGunMuzzleFlashIsActive = false;
        this.sniperGunMuzzleFlashIsActive = false;
        this.leftGunMuzzleFlashTimer = 0;
        this.rightGunMuzzleFlashTimer = 0;
        this.rifleGunMuzzleFlashTimer = 0;
        this.shotgunGunMuzzleFlashTimer = 0;
        this.sniperGunMuzzleFlashTimer = 0;
    }

    _initLaserColors() {
        const prestige = this.networkHandler ? this.networkHandler.prestige : 0;
        const level = this.networkHandler ? this.networkHandler.level : 1;
        const color = this._getEquippedLaserColor();

        // PERF: Use cached MeshComponent refs from start() instead of per-call getComponent()
        for (let i = 0; i < this._laserMeshCache.length; i++) {
            const mesh = this._laserMeshCache[i];
            if (mesh && mesh.material) {
                mesh.material.ambientColor = color;
                mesh.material.diffuseColor = color;
                mesh.material.emissiveColor = color;
            }
        }

        // Also sync the desktop HTML crosshair color when laser colors change
        if (!this._isXRActive()) {
            this._updateDesktopCrosshair(level, prestige);
        }
    }

    /**
     * Debug function to toggle between laser colors and active states
     * Cycles through: off, prestige 0, prestige 1, prestige 2, prestige 3 every 3 seconds
     */
    debugToggleLaserColors() {
        // Clear any existing debug interval
        if (this._debugLaserInterval) {
            clearInterval(this._debugLaserInterval);
            this._debugLaserInterval = null;
        }

        if (!this.debugLaserToggle) return; // Only run if debug property is enabled

        // Initialize debug state
        this._debugLaserState = 0;

        // Start interval to cycle through laser states every 3 seconds
        this._debugLaserInterval = setInterval(() => {
            this._cycleDebugLaserState();
        }, 3000); // 3 seconds

        // Immediately show first state
        this._cycleDebugLaserState();
    }

    /**
     * Internal function to cycle through debug laser states
     */
    _cycleDebugLaserState() {
        // Cycle through states: 0=off, 1=test green, 2=prestige1 blue, 3=prestige2 purple, 4=prestige3 gold
        this._debugLaserState = (this._debugLaserState || 0) + 1;
        if (this._debugLaserState > 4) this._debugLaserState = 0;

        const laserObjects = [
            this.laserLeftGun,
            this.laserRightGun,
            this.laserRifleGun,
            this.shotgunLaser,
            this.sniperLaser
        ];

        if (this._debugLaserState === 0) {
            // Turn all lasers OFF
            laserObjects.forEach(laser => {
                if (laser) laser.active = false;
            });
        } else {
            // Turn lasers ON and set color based on state
            let color;
            let description;

            switch (this._debugLaserState) {
                case 1:
                    color = [0.0, 1.0, 0.0, 1]; // Pure green for testing (different from default neon green)
                    description = 'Test Green';
                    break;
                case 2:
                    color = ProgressionSystem.getLaserColor(1); // Blue
                    description = 'Neon Blue';
                    break;
                case 3:
                    color = ProgressionSystem.getLaserColor(2); // Purple
                    description = 'Neon Purple';
                    break;
                case 4:
                    color = ProgressionSystem.getLaserColor(3); // Gold
                    description = 'Neon Gold';
                    break;
            }

            // PERF: Use cached laser objects + mesh refs instead of per-call getComponent()
            for (let i = 0; i < this._laserObjects.length; i++) {
                const laser = this._laserObjects[i];
                if (laser) {
                    laser.active = true;
                    const mesh = this._laserMeshCache[i];
                    if (mesh && mesh.material) {
                        mesh.material.ambientColor = color;
                        mesh.material.diffuseColor = color;
                        mesh.material.emissiveColor = color;
                    } else {
                        console.warn(`[DEBUG] No material found for laser ${laser.name || 'unknown'}`);
                    }
                } else {
                    console.warn('[DEBUG] Laser object is null');
                }
            }
        }
    }

    _initInputListeners() {
        this._onKeyDown = (e) => {
            this._keys.add(e.code);
            // Number keys for direct weapon slot selection (desktop only)
            if (!this._isXRActive()) {
                if (e.code === 'Digit1') this._selectWeaponSlot(0);
                else if (e.code === 'Digit2') this._selectWeaponSlot(1);
                else if (e.code === 'Digit3') this._selectWeaponSlot(2);
                else if (e.code === 'Digit4') this._selectWeaponSlot(3);
            }
        };
        this._onKeyUp = (e) => this._keys.delete(e.code);
        this._onBlur = () => {
            this._keys.clear();
            this._mouseLeftDown = false;
            this._mouseRightDown = false;
        };
        window.addEventListener('keydown', this._onKeyDown);
        window.addEventListener('keyup', this._onKeyUp);
        window.addEventListener('blur', this._onBlur);

        // NOTE: All mouse/ADS state variables, pre-allocated vectors, ADS configs,
        // and NonVrCamera ref are initialized in init() and cached in start().
        // No duplicate allocations here — only DOM event listeners below.

        // Expose blend globally so rifle-orient can read it
        window._adsBlend = 0;
        window._adsWeapon = 'default';

        const canvas = document.getElementById('canvas');
        if (canvas) {
            this._onCanvasClick = () => {
                if (window.DESKTOP_MODE_ENABLED && !this._isXRActive() && !this._isPointerLocked) {
                    canvas.requestPointerLock();
                }
            };
            canvas.addEventListener('click', this._onCanvasClick);
        }

        this._onPointerLockChange = () => {
            this._isPointerLocked = document.pointerLockElement === (document.getElementById('canvas'));
            // Delegate desktop crosshair visibility to progression-aware method
            this._updateDesktopCrosshairVisibility();
        };
        document.addEventListener('pointerlockchange', this._onPointerLockChange);

        this._onMouseMove = (e) => {
            if (!this._isFlatLookActive()) return;
            this._mouseDeltaX += e.movementX;
            this._mouseDeltaY += e.movementY;
        };
        document.addEventListener('mousemove', this._onMouseMove);

        this._onMouseDown = (e) => {
            if (!this._isFlatLookActive()) return;
            if (e.button === 0) this._mouseLeftDown = true;
            if (e.button === 2) this._mouseRightDown = true;
        };
        this._onMouseUp = (e) => {
            if (e.button === 0) this._mouseLeftDown = false;
            if (e.button === 2) this._mouseRightDown = false;
        };
        document.addEventListener('mousedown', this._onMouseDown);
        document.addEventListener('mouseup', this._onMouseUp);

        // Prevent context menu on right-click when pointer locked
        this._onContextMenu = (e) => {
            if (this._isPointerLocked) e.preventDefault();
        };
        document.addEventListener('contextmenu', this._onContextMenu);

        // Mouse wheel for weapon cycling
        this._onWheel = (e) => {
            if (!this._isFlatLookActive()) return;
            if (e.deltaY > 0) {
                this._cycleWeaponSlot(1);
            } else if (e.deltaY < 0) {
                this._cycleWeaponSlot(-1);
            }
        };
        document.addEventListener('wheel', this._onWheel);
    }

    /** Check if an XR session is currently active */
    _isXRActive() {
        return !!(this.engine.xr && this.engine.xr.session);
    }

    /**
     * True whenever the flat-screen (non-VR) FPS look pipeline should be active.
     * Desktop uses Pointer Lock for that; on mobile we have no Pointer Lock at all,
     * so MOBILE_MODE_ENABLED acts as the equivalent gate. This lets mouse-look /
     * ADS / crosshair code paths run identically for both inputs.
     */
    _isFlatLookActive() {
        return !this._isXRActive() && (this._isPointerLocked || !!window.MOBILE_MODE_ENABLED);
    }

    /** Select a weapon slot directly by index (1-4 keys) */
    _selectWeaponSlot(slotIndex) {
        if (slotIndex < 0 || slotIndex >= 4) return;
        if (this._weaponSlots[slotIndex] === null) return;
        if (this._currentSlotIndex === slotIndex) return;
        if (this._isSwitchingWeapon) return;

        this._isSwitchingWeapon = true;
        this.fireLeftGun = false;
        this.fireRightGun = false;
        this.fireRifle = false;
        this.fireShotgun = false;
        this._sniperTriggerPressed = false;
        this._shotgunTriggerPressed = false;

        this._updateAimAssistVisibility();
        this._setMeshesActive(this.leftGun, false);
        this._setMeshesActive(this.rightGun, false);
        this._setMeshesActive(this.rifleGun, false);
        this._setMeshesActive(this.shotgunGun, false);
        this._setMeshesActive(this.sniperGun, false);

        // QUEST 2 OPTIMIZATION: dt-based timer replaces setTimeout closure
        this._weaponSwitchTimer = 0.2;
        this._weaponSwitchPendingSlot = slotIndex;
        this._weaponSwitchPendingType = 'select';
    }

    /** Cycle weapon slot forward or backward (mouse wheel) */
    _cycleWeaponSlot(direction) {
        if (this._isSwitchingWeapon) return;
        let nextSlot = this._currentSlotIndex;
        for (let i = 0; i < 4; i++) {
            nextSlot = (nextSlot + direction + 4) % 4;
            if (this._weaponSlots[nextSlot] !== null) {
                this._selectWeaponSlot(nextSlot);
                return;
            }
        }
    }

    onDestroy() {
        if (this._onKeyDown) window.removeEventListener('keydown', this._onKeyDown);
        if (this._onKeyUp) window.removeEventListener('keyup', this._onKeyUp);
        if (this._onBlur) window.removeEventListener('blur', this._onBlur);
        if (this._onMouseMove) document.removeEventListener('mousemove', this._onMouseMove);
        if (this._onMouseDown) document.removeEventListener('mousedown', this._onMouseDown);
        if (this._onMouseUp) document.removeEventListener('mouseup', this._onMouseUp);
        if (this._onContextMenu) document.removeEventListener('contextmenu', this._onContextMenu);
        if (this._onPointerLockChange) document.removeEventListener('pointerlockchange', this._onPointerLockChange);
        if (this._onWheel) document.removeEventListener('wheel', this._onWheel);
        const canvas = document.getElementById('canvas');
        if (canvas && this._onCanvasClick) canvas.removeEventListener('click', this._onCanvasClick);
        if (this._boundOnXRSessionEnd && this.engine && this.engine.onXRSessionEnd) {
            this.engine.onXRSessionEnd.remove(this._boundOnXRSessionEnd);
        }
        if (this._boundOnXRSessionStart && this.engine && this.engine.onXRSessionStart) {
            this.engine.onXRSessionStart.remove(this._boundOnXRSessionStart);
        }
        if (this._boundOnDesktopSessionEnd) {
            window.removeEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
            window.removeEventListener('mobile-session-end', this._boundOnDesktopSessionEnd);
        }
        if (this._boundOnDesktopSessionStart) {
            window.removeEventListener('desktop-session-start', this._boundOnDesktopSessionStart);
            window.removeEventListener('mobile-session-start', this._boundOnDesktopSessionStart);
        }
        if (window._fpsMovement === this) window._fpsMovement = null;
    }

    _initAudio() {
        // Only re-lookup controller-dependent refs (XR input sources change per session)
        // Stable audio/weapon refs are cached once in start() (_stableAudioCached)
        this.leftGunSound = this.leftController.getComponent(HowlerAudioSource);
        this.rightGunSound = this.rightController.getComponent(HowlerAudioSource);
        // PERF: Cache input components to avoid per-frame getComponent('input') in _getInput
        this._cachedLeftInput = this.leftController.getComponent('input');
        this._cachedRightInput = this.rightController.getComponent('input');
    }

    /** Desktop audio init: guns have their own audio sources, controllers may not exist */
    _initAudioDesktop() {
        // Only re-lookup controller-dependent refs; stable refs cached in start()
        this.leftGunSound = this.leftController?.getComponent?.(HowlerAudioSource)
            || this.leftGun?.getComponent?.(HowlerAudioSource);
        this.rightGunSound = this.rightController?.getComponent?.(HowlerAudioSource)
            || this.rightGun?.getComponent?.(HowlerAudioSource);
        // PERF: Cache input components (may be null in desktop mode)
        this._cachedLeftInput = this.leftController?.getComponent?.('input') || null;
        this._cachedRightInput = this.rightController?.getComponent?.('input') || null;
    }

    /** PERF: Cache rifle-orient and sniper audio components to avoid per-shot WASM lookups */
    _cacheWeaponComponents() {
        // No-op: stable weapon components are now cached once in start()
        // Kept for backward compatibility in case external code calls this.
    }

    _initNetwork() {
        this.networkHandler = this.network.getComponent('arena-handler');

        // Initialize weapon slots based on current progression level
        if (this.networkHandler) {
            setTimeout(() => {
                // Wait a bit for network to fully initialize
                this.resetWeapons();
            }, 100);
        }
    }

    teleportToLobby() {
        if (!this._lobbySpawnPos) return;
        // Clear respawning flag so update() is not blocked
        this.isRespawning = false;
        if (this._physx) this._physx.kinematic = true;
        // Cancel any pending kinematic restore from a previous respawn()
        if (this._kinematicTimeoutId) {
            clearTimeout(this._kinematicTimeoutId);
            this._kinematicTimeoutId = null;
        }
        if (this._physx) {
            if (this._physx.linearVelocity) { this._physx.linearVelocity[0] = 0; this._physx.linearVelocity[1] = 0; this._physx.linearVelocity[2] = 0; }
            if (this._physx.angularVelocity) { this._physx.angularVelocity[0] = 0; this._physx.angularVelocity[1] = 0; this._physx.angularVelocity[2] = 0; }
        }
        this.object.setPositionWorld(this._lobbySpawnPos);
        this.playerRig.setRotationWorld(this._lobbySpawnRot);
        // Reset desktop camera yaw to match lobby facing direction (-67.435° Y euler)
        if (this._desktopYaw !== undefined) {
            this._desktopYaw = -67.435 * (Math.PI / 180);
        }
        if (this._physx) {
            this._kinematicTimeoutId = setTimeout(() => { this._kinematicTimeoutId = null; if (this._physx) this._physx.kinematic = false; }, 100);
        }
    }

    respawn(pos, rot) {
        this._lastJumpTime = 0;
        this._groundGraceDelay = 500;
        this.isRespawning = true;
        this._physx.kinematic = true;
        this.object.setPositionWorld(pos);
        this.playerRig.setRotationWorld(rot);
        this._jumpCount = 0;
        this._coyoteTimer = this.coyoteTime;
        this._jumpBufferTimer = 0;
        this._wasJumpPressed = false;
        this._onGround = false;
        this._wallStuck = false;
        this._currentEndurance = this.maxEndurance;
        this.fireLeftGun = false;
        this.fireRightGun = false;
        this.fireRifle = false;
        this.fireShotgun = false;
        this._shotgunTriggerPressed = false;
        if (this._physx && this._physx.linearVelocity) {
            this._physx.linearVelocity[0] = 0;
            this._physx.linearVelocity[1] = 0;
            this._physx.linearVelocity[2] = 0;
        }
        if (this._physx && this._physx.angularVelocity) {
            this._physx.angularVelocity[0] = 0;
            this._physx.angularVelocity[1] = 0;
            this._physx.angularVelocity[2] = 0;
        }
        this._checkGround();

        let updates = 0;
        const sendUpdate = () => {
            if (this.networkHandler && this.networkHandler.lobby) {
                this.playerRig.getRotationWorld(this._playerRigRot);
                this.networkHandler.lobby.data.sendToAll({
                    type: "playerUpdate",
                    payload: {
                        id: this.networkHandler.userId,
                        pos: pos,
                        rot: this._playerRigRot,
                        t: this.networkHandler._elapsedMs || 0,
                        justRespawned: true
                    }
                });
            }
            updates++;
            if (updates < 3) setTimeout(sendUpdate, 64);
        };
        sendUpdate();
        // Clear any previous kinematic timeout to avoid race conditions on session end
        if (this._kinematicTimeoutId) clearTimeout(this._kinematicTimeoutId);
        this._kinematicTimeoutId = setTimeout(() => {
            this._kinematicTimeoutId = null;
            this._physx.kinematic = false;
            this.isRespawning = false;
        }, 100);
        // Reset manual aim assist flag so optimal state is applied on respawn
        this._aimAssistManuallySet = false;
        // Set up optimal loadout based on current unlocks
        this._setupOptimalLoadout();
        this._updateWeaponMeshes();
        this._updateIconHighlights();
        this._updateAimAssistVisibility();
    }

    update(dt) {
        // Increment dt-based cooldown timers (weapon fire)
        if (this._shotgunFireTimer > 0) this._shotgunFireTimer -= dt;
        if (this._sniperFireTimer > 0) this._sniperFireTimer -= dt;
        // APP TIME OPTIMIZATION: dt-based accumulator replaces Date.now() syscall
        this._frameTime += dt * 1000;

        // Controller/component polling and late initialization
        if (!this._controllersInitialized) {
            // In flat-screen mode (no XR), skip controller requirement entirely
            if (!this._isXRActive()) {
                this._initAudioDesktop();
                this._controllersInitialized = true;
            } else if (this.leftController && this.rightController &&
                this.leftController.getComponent && this.rightController.getComponent &&
                this.leftController.getComponent('input') && this.rightController.getComponent('input')) {
                this._initAudio();
                // Reset akimbo gun local transforms to editor values so they
                // sit correctly on the VR hand controllers after desktop mode
                // repositioned them via world-space transforms.
                this._resetGunLocalsForXR();
                this._controllersInitialized = true;
            } else {
                // Not ready yet, skip update
                return;
            }
        }
        if (this.isRespawning) return;

        // PERF: Decrement dt-based fire cooldowns for pistol/rifle (replaces setTimeout closures)
        if (this._leftGunFireTimer > 0) {
            this._leftGunFireTimer -= dt;
            if (this._leftGunFireTimer <= 0) this.fireLeftGun = false;
        }
        if (this._rightGunFireTimer > 0) {
            this._rightGunFireTimer -= dt;
            if (this._rightGunFireTimer <= 0) this.fireRightGun = false;
        }
        if (this._rifleFireTimer > 0) {
            this._rifleFireTimer -= dt;
            if (this._rifleFireTimer <= 0) this.fireRifle = false;
        }

        // QUEST 2 OPTIMIZATION: dt-based weapon switch timer (replaces setTimeout closure)
        if (this._weaponSwitchTimer > 0) {
            this._weaponSwitchTimer -= dt;
            if (this._weaponSwitchTimer <= 0) {
                const type = this._weaponSwitchPendingType;
                if (type === 'select') {
                    this._currentSlotIndex = this._weaponSwitchPendingSlot;
                    this._updateWeaponMeshes();
                    if (this.unlockSound) this.unlockSound.play();
                    this._applyOptimalAimAssistForWeapon();
                    this._isSwitchingWeapon = false;
                    this._updateAimAssistVisibility();
                    this._updateIconHighlights();
                } else if (type === 'cycle') {
                    let nextSlotIndex = (this._currentSlotIndex + 1) % 4;
                    let attempts = 0;
                    const slots = this._weaponSlots; // Cache array ref
                    while (slots[nextSlotIndex] === null && attempts < 4) {
                        nextSlotIndex = (nextSlotIndex + 1) % 4;
                        attempts++;
                    }
                    if (slots[nextSlotIndex] !== null) {
                        this._currentSlotIndex = nextSlotIndex;
                    }
                    this._updateWeaponMeshes();
                    if (this.unlockSound) this.unlockSound.play();
                    this._applyOptimalAimAssistForWeapon();
                    this._isSwitchingWeapon = false;
                    this._updateAimAssistVisibility();
                    this._updateIconHighlights();
                    this._weaponSwitchToggle = false;
                }
                this._weaponSwitchPendingType = null;
                this._weaponSwitchPendingSlot = -1;
            }
        }

        // Local UI reference cache to avoid repeated 'this.' lookups
        const infoText = this.gameInfoText;

        // QUEST 2 OPTIMIZATION: dt-based pickup message timer
        if (this._pickupMessageTimer > 0) {
            this._pickupMessageTimer -= dt;
            // Use fast string matching instead of recreating string every frame
            if (this._pickupMessageTimer <= 0 && infoText && infoText.active) {
                if (infoText.text.startsWith('Picked up:')) {
                    infoText.active = false;
                    infoText.text = '';
                }
            }
        }

        // QUEST 2 OPTIMIZATION: dt-based loadout setup cooldown
        if (this._loadoutResetTimer > 0) {
            this._loadoutResetTimer -= dt;
            if (this._loadoutResetTimer <= 0) {
                this._loadoutSetupInProgress = false;
            }
        }

        // PERFORMANCE: Throttled physics and movement checks
        this._updateTimers.ground += dt;
        this._updateTimers.pickups += dt;
        this._updateTimers.ui += dt;

        if (this._updateTimers.ground >= this._updateIntervals.ground) {
            this._updateTimers.ground = 0;
            this._checkGround();
        }

        if (this._updateTimers.pickups >= this._updateIntervals.pickups) {
            this._updateTimers.pickups = 0;
            this._checkPickups();
        }

        this._updateMuzzleFlashes(dt);
        const input = this._getInput();

        // Edge detection for E key
        const ePressed = this._keys.has('KeyE');
        const justPressedE = ePressed && !this._prevEPressed;
        this._prevEPressed = ePressed;

        const overlaps = this._overlappingPickups;
        const hasOverlaps = overlaps.length > 0;

        // Show pickup prompt if overlapping a pickup
        if (hasOverlaps) {
            if (infoText) {
                const pickupName = overlaps[0].name;
                let weaponType = null;

                // PERFORMANCE: Switch cases are heavily optimized by V8 engines vs chained 'if's
                switch (pickupName) {
                    case 'RiflePickup': weaponType = 'rifle'; break;
                    case 'ShotgunPickup': weaponType = 'shotgun'; break;
                    case 'SniperPickup': weaponType = 'sniper'; break;
                    case 'AkimboPickup': weaponType = 'default'; break;
                }

                if (weaponType) {
                    // PERFORMANCE: Array caching and optimized slot counting loop
                    let slotsFilled = 0;
                    const slots = this._weaponSlots;
                    const len = slots.length;
                    for (let i = 0; i < len; i++) {
                        if (slots[i]) slotsFilled++;
                    }

                    const key = this._isXRActive() ? 'X' : 'E';
                    const newText = slotsFilled < 2 ? `Press ${key} to Pick Up Weapon` : `Press ${key} to swap`;

                    // GPU OPTIMIZATION: Only update text if it actually changes
                    if (infoText.text !== newText) infoText.text = newText;
                    if (!infoText.active) infoText.active = true;
                }
            }
        } else if (infoText && infoText.active && !infoText.text.startsWith('Picked up:')) {
            // GPU OPTIMIZATION: Only update if actually needs to change
            infoText.active = false;
            if (infoText.text !== '') infoText.text = '';
        }

        // Contextual X button (VR/gamepad) or E key (keyboard): pickup or switch
        const gamepadPickupPressed = input.pickupPressed && !ePressed;
        if (gamepadPickupPressed || justPressedE) {
            if (hasOverlaps) {
                let weaponType = null;
                switch (overlaps[0].name) {
                    case 'RiflePickup': weaponType = 'rifle'; break;
                    case 'ShotgunPickup': weaponType = 'shotgun'; break;
                    case 'SniperPickup': weaponType = 'sniper'; break;
                    case 'AkimboPickup': weaponType = 'default'; break;
                }
                if (weaponType) this._addWeaponToSlot(weaponType);
            } else {
                this._handleWeaponSwitch(true, justPressedE);
            }
        }

        this._handleRotation(input.rot, dt);
        this._updateAkimboDesktop();
        this._updateADS(dt);
        this._handleAimAssistSwitch(input.switchAimAssist);

        // Cache vector references to avoid repeated property lookups
        const tVel = this._tempVelocity;
        const tMove = this._tempDesiredMove;

        vec3.copy(tVel, this._physx.linearVelocity);
        this._handleJump(dt, input.jumpPressed, tVel);
        this._updateMoveDirection(input.x, input.y);

        const canSprint = this._adsBlend < 0.1;
        // This now returns a true/false based on the strict state machine above
        const isSprinting = this._handleSprinting(canSprint && input.sprint, dt);

        // The speed will now drop the moment isActuallySprinting returns false
        let moveSpeed = this.speed * (isSprinting ? 1.5 : 1.0);
        // PERFORMANCE: Resolve the current weapon ONCE here, use it for ADS logic AND Fire logic below
        const currentWeapon = this._weaponSlots[this._currentSlotIndex] || 'default';

        // ADS move speed reduction
        if (this._adsBlend > 0) {
            const cfg = this._adsConfigs[currentWeapon] || this._adsConfigs['default'];
            moveSpeed *= 1.0 - (1.0 - cfg.moveScale) * this._adsBlend;
        }

        // Math combination to scale vector correctly
        const controlFactor = this._onGround ? 1.0 : this.airControl;
        const finalSpeed = moveSpeed * controlFactor;

        vec3.scale(tMove, this._moveDir, finalSpeed);
        this._applySlopeAdjustment(tMove);

        if (!this.isAirborne) {
            tVel[0] = tMove[0];
            tVel[2] = tMove[2];
        }

        if (input.x === 0 && input.y === 0 && !this.isAirborne) {
            tVel[0] = 0;
            tVel[2] = 0;
        }

        if ((this.fireLeftGun || this.fireRightGun || this.fireRifle || this.fireShotgun || this.fireSniper) && !this.isAirborne) {
            tVel[0] *= 0.8;
            tVel[2] *= 0.8;
        }
        this._physx.linearVelocity = tVel;

        // Fire and Shooting Behavior
        // PERFORMANCE: Using the cached 'currentWeapon' and a Switch statement for direct jump routing
        switch (currentWeapon) {
            case 'shotgun':
                this._handleShotgunFire(input);
                break;
            case 'rifle':
                this._handleRifleFire(input);
                break;
            case 'sniper':
                this._handleSniperFire(input);
                break;
            case 'default':
            default:
                this._handleLeftFire(input);
                this._handleRightFire(input);
                break;
        }
    }
    _getInput() {
        // PERF: Use cached input components instead of per-frame getComponent() WASM lookups
        const leftInput = this._cachedLeftInput;
        const rightInput = this._cachedRightInput;
        const gamepadLeft = leftInput?.xrInputSource?.gamepad;
        const gamepadRight = rightInput?.xrInputSource?.gamepad;
        const x = gamepadLeft?.axes?.[2] ?? (this._keys.has('KeyA') ? -1 : this._keys.has('KeyD') ? 1 : 0);
        const y = -(gamepadLeft?.axes?.[3] ?? (this._keys.has('KeyW') ? -1 : this._keys.has('KeyS') ? 1 : 0));
        // Desktop: arrow keys only when pointer is NOT locked (when locked, mouse handles rotation)
        const keyRot = this._keys.has('ArrowLeft') ? -1 : this._keys.has('ArrowRight') ? 1 : 0;
        const rot = gamepadRight?.axes?.[2] ?? keyRot;
        const jumpPressed = gamepadRight?.buttons?.[1]?.pressed || this._keys.has('Space');
        const sprint = gamepadLeft?.buttons?.[1]?.pressed || this._keys.has('ShiftLeft');
        const switchAimAssist = gamepadRight?.buttons?.[4]?.pressed || this._keys.has('KeyR');
        // Use button index 4 for pickup/switch (X button)
        const pickupPressed = gamepadLeft?.buttons?.[4]?.pressed || this._keys.has('KeyE');

        // Desktop mouse fire: left click fires current weapon (acts as both triggers)
        // When no XR gamepad is present, mouse click provides the trigger input
        const hasXRGamepad = !!(gamepadLeft || gamepadRight);
        
        if (!hasXRGamepad && this._mouseLeftDown) {
            this._desktopMouseTrigger.pressed = true;
        } else {
            this._desktopMouseTrigger.pressed = false;
        }
        const mouseTrigger = this._desktopMouseTrigger.pressed ? this._desktopMouseTrigger : null;
        
        const leftTrigger = gamepadLeft?.buttons?.[0] || mouseTrigger;
        const rightTrigger = gamepadRight?.buttons?.[0] || mouseTrigger;

        this._inputState.x = x;
        this._inputState.y = y;
        this._inputState.rot = rot;
        this._inputState.jumpPressed = jumpPressed;
        this._inputState.sprint = sprint;
        this._inputState.switchAimAssist = switchAimAssist;
        this._inputState.leftTrigger = leftTrigger;
        this._inputState.rightTrigger = rightTrigger;
        this._inputState.pickupPressed = pickupPressed;

        return this._inputState;
    }

    _handleRotation(rot, dt) {
        // --- Flat-screen look (desktop pointer lock OR mobile touch joystick) ---
        if (this._isFlatLookActive()) {
            // ADS sensitivity reduction
            let sens = this._mouseSensitivity;
            if (this._adsBlend > 0) {
                const weapon = this._weaponSlots[this._currentSlotIndex] || 'default';
                const cfg = this._adsConfigs[weapon] || this._adsConfigs['default'];
                sens *= 1.0 - (1.0 - cfg.sens) * this._adsBlend;
            }
            // Accumulate mouse delta into persistent yaw/pitch angles
            this._desktopYaw += -this._mouseDeltaX * sens;
            this._headPitch += -this._mouseDeltaY * sens;
            // Clamp pitch to ±89° (avoids gimbal lock at extremes)
            const maxPitch = 89 * Math.PI / 180;
            if (this._headPitch > maxPitch) this._headPitch = maxPitch;
            if (this._headPitch < -maxPitch) this._headPitch = -maxPitch;

            this._applyDesktopRotation();

            // Consume mouse delta
            this._mouseDeltaX = 0;
            this._mouseDeltaY = 0;
        }

        // --- Gamepad / arrow key rotation (used in VR or flat-screen without pointer lock) ---
        if (this.playerRig && Math.abs(rot) > 0.05) {
            if (this._isFlatLookActive()) {
                // Arrow keys (or synthesised mobile look-pad) add to desktop yaw
                this._desktopYaw += -rot * this.rotationSpeed * dt;
                this._applyDesktopRotation();
            } else {
                const yaw = -rot * this.rotationSpeed * dt;
                this.playerRig.getRotationWorld(this._tempRigRot);
                quat.setAxisAngle(this._tempDeltaRot, this._up, yaw);
                quat.multiply(this._tempRigRot, this._tempDeltaRot, this._tempRigRot);
                this.playerRig.setRotationWorld(this._tempRigRot);
            }
        }
    }

    /**
     * Apply desktop rotation state to scene objects:
     * - NonVrCamera: full yaw + pitch (what the player sees)
     * - EyeLeft (head): full yaw + pitch (weapons & GUI children follow aim)
     * - Rig (playerRig): untouched
     */
    _applyDesktopRotation() {
        const pitchDeg = this._headPitch * (180 / Math.PI);
        const yawDeg = this._desktopYaw * (180 / Math.PI);

        // NonVrCamera gets full yaw + pitch
        if (this._nonVrCamera) {
            quat.fromEuler(this._headPitchQuat, pitchDeg, yawDeg, 0);
            this._nonVrCamera.setRotationLocal(this._headPitchQuat);
        }

        // EyeLeft (head) gets full yaw + pitch so all children (guns, GUI) follow aim
        if (this.head) {
            quat.fromEuler(this._headYawQuat, pitchDeg, yawDeg, 0);
            this.head.setRotationLocal(this._headYawQuat);
        }
    }

    /**
     * In desktop mode, position akimbo pistols relative to the camera
     * since they are children of the VR hand controllers which aren't tracked.
     */
    _updateAkimboDesktop() {
        if (this._isXRActive()) return;
        const cam = this._nonVrCamera || this.head;
        if (!cam) return;

        cam.getPositionWorld(this._akimboDesktopPos);
        cam.getForwardWorld(this._akimboDesktopForward);
        cam.getRightWorld(this._akimboDesktopRight);

        // Left gun: forward 0.3, down 0.225, left 0.20
        if (this.leftGun) {
            vec3.scaleAndAdd(this.pos, this._akimboDesktopPos, this._akimboDesktopForward, 0.3);
            this.pos[1] -= 0.225;
            vec3.scaleAndAdd(this.pos, this.pos, this._akimboDesktopRight, -0.20);
            this.leftGun.setPositionWorld(this.pos);
            vec3.scaleAndAdd(this.pos2, this._akimboDesktopPos, this._akimboDesktopForward, -10.0);
            this.leftGun.lookAt(this.pos2);
        }

        // Right gun: forward 0.3, down 0.225, right 0.20
        if (this.rightGun) {
            vec3.scaleAndAdd(this.pos, this._akimboDesktopPos, this._akimboDesktopForward, 0.3);
            this.pos[1] -= 0.225;
            vec3.scaleAndAdd(this.pos, this.pos, this._akimboDesktopRight, 0.20);
            this.rightGun.setPositionWorld(this.pos);
            vec3.scaleAndAdd(this.pos2, this._akimboDesktopPos, this._akimboDesktopForward, -10.0);
            this.rightGun.lookAt(this.pos2);
        }
    }

    /**
     * Reset akimbo gun local transforms to editor values when entering XR.
     * Desktop mode uses setPositionWorld/lookAt which corrupts local transforms,
     * so we must restore them for the VR hand controllers.
     * Editor values: translation [0,0,0], rotation [-120, 0, 180] Euler, scale [0.1, 0.1, 0.1].
     */
    _resetGunLocalsForXR() {
        // Cache original editor scales on first call (before desktop mode corrupts them)
        if (!this._gunEditorScalesCached) {
            this._gunEditorScales = new Map();
            for (const gun of [this.leftGun, this.rightGun, this.rifleGun, this.shotgunGun, this.sniperGun]) {
                if (!gun) continue;
                const s = gun.getScalingLocal();
                this._gunEditorScales.set(gun, new Float32Array([s[0], s[1], s[2]]));
            }
            this._gunEditorScalesCached = true;
        }
        // PERF: Use pre-allocated quats from init() instead of creating new ones per call
        for (const gun of [this.leftGun, this.rightGun]) {
            if (!gun) continue;
            gun.setPositionLocal(this._zeroScale);
            gun.setRotationLocal(this._akimboRot);
            const s = this._gunEditorScales.get(gun);
            if (s) gun.setScalingLocal(s);
        }
        for (const gun of [this.rifleGun, this.shotgunGun, this.sniperGun]) {
            if (!gun) continue;
            gun.setPositionLocal(this._zeroScale);
            gun.setRotationLocal(this._rifleRot);
            const s = this._gunEditorScales.get(gun);
            if (s) gun.setScalingLocal(s);
        }
    }

    /**
     * Update ADS (Aim Down Sights) state each frame.
     * Smoothly lerps blend factor, FOV, and crosshair CSS classes.
     */
    _updateADS(dt) {
        if (this._isXRActive()) return;

        const currentWeapon = this._weaponSlots[this._currentSlotIndex] || 'default';

        // No ADS for akimbo/default — only rifles/shotgun/sniper
        if (currentWeapon === 'default') {
            this._adsActive = false;
            if (this._adsBlend > 0) {
                this._adsBlend = 0;
                window._adsBlend = 0;
                if (this._adsViewComponent && this._adsFovInitialized) {
                    this._adsViewComponent.fov = this._baseFov;
                }
                const ch = this._crosshairEl;
                if (ch) ch.classList.remove('ads', 'ads-sniper');
                if (this._adsLeftGunHidden) {
                    this._setMeshesActive(this.leftGun, true);
                    this._adsLeftGunHidden = false;
                }
            }
            return;
        }

        this._adsActive = this._mouseRightDown && this._isFlatLookActive();

        // Lerp blend factor toward target (frame-rate independent exponential smoothing)
        const target = this._adsActive ? 1.0 : 0.0;
        const speed = this._adsActive ? this._adsLerpSpeed : this._adsOutSpeed;
        this._adsBlend += (target - this._adsBlend) * (1.0 - Math.exp(-speed * dt));
        // Snap to endpoints to avoid perpetual micro-lerp
        if (this._adsBlend < 0.001) this._adsBlend = 0;
        if (this._adsBlend > 0.999) this._adsBlend = 1;

        // Expose to rifle-orient and other components
        window._adsBlend = this._adsBlend;
        window._adsWeapon = currentWeapon;

        // --- FOV ---
        if (!this._adsFovInitialized && this._nonVrCamera) {
            this._adsViewComponent = this._nonVrCamera.getComponent('view');
            if (this._adsViewComponent) {
                this._baseFov = this._adsViewComponent.fov;
                this._adsFovInitialized = true;
            }
        }
        if (this._adsViewComponent) {
            const cfg = this._adsConfigs[currentWeapon] || this._adsConfigs['default'];
            const targetFov = this._baseFov + (cfg.fov - this._baseFov) * this._adsBlend;
            this._adsViewComponent.fov = targetFov;
        }

        // --- Crosshair CSS classes ---
        const crosshairEl = this._crosshairEl;
        if (crosshairEl) {
            if (this._adsBlend > 0.1) {
                if (currentWeapon === 'sniper') {
                    crosshairEl.classList.remove('ads');
                    crosshairEl.classList.add('ads-sniper');
                } else {
                    crosshairEl.classList.remove('ads-sniper');
                    crosshairEl.classList.add('ads');
                }
            } else {
                crosshairEl.classList.remove('ads', 'ads-sniper');
            }
        }
    }

    _handleWeaponSwitch(forceSwitch, isKeyboard = false) {
        if (!isKeyboard && this._isSwitchingWeapon) return;

        // PERFORMANCE: Count equipped weapons without creating new array
        let equippedCount = 0;
        for (let i = 0; i < this._weaponSlots.length; i++) {
            if (this._weaponSlots[i] !== null) equippedCount++;
        }

        // Only allow switching if we have more than 1 equipped weapon
        if (equippedCount <= 1) return;

        if ((isKeyboard || (forceSwitch && !this._weaponSwitchToggle))) {
            if (!isKeyboard) this._weaponSwitchToggle = true;
            this._isSwitchingWeapon = true;
            this.fireLeftGun = false;
            this.fireRightGun = false;
            this.fireRifle = false;
            this.fireShotgun = false;
            this._sniperTriggerPressed = false;

            // Turn off all lasers/crosshairs before hiding weapon meshes
            this._updateAimAssistVisibility();

            // Hide all weapon meshes
            this._setMeshesActive(this.leftGun, false);
            this._setMeshesActive(this.rightGun, false);
            this._setMeshesActive(this.rifleGun, false);
            this._setMeshesActive(this.shotgunGun, false);
            this._setMeshesActive(this.sniperGun, false);

            // QUEST 2 OPTIMIZATION: dt-based timer replaces setTimeout closure
            this._weaponSwitchTimer = 0.35;
            this._weaponSwitchPendingSlot = this._currentSlotIndex;
            this._weaponSwitchPendingType = 'cycle';

        } else if (!forceSwitch && !isKeyboard) {
            this._weaponSwitchToggle = false;
        }
    }

    _setMeshesActive(gun, active) {
        // GPU OPTIMIZATION: Only change mesh visibility if state actually changes
        let weaponType = null;
        let cachedMeshes = null;

        if (gun === this.rifleGun) {
            weaponType = 'rifle';
            if (!this._rifleMeshes) this._rifleMeshes = gun.getComponents(MeshComponent);
            cachedMeshes = this._rifleMeshes;
        } else if (gun === this.shotgunGun) {
            weaponType = 'shotgun';
            if (!this._shotgunMeshes) this._shotgunMeshes = gun.getComponents(MeshComponent);
            cachedMeshes = this._shotgunMeshes;
        } else if (gun === this.sniperGun) {
            weaponType = 'sniper';
            if (!this._sniperMeshes) this._sniperMeshes = gun.getComponents(MeshComponent);
            cachedMeshes = this._sniperMeshes;
        } else if (gun === this.leftGun) {
            // PERF: Cache akimbo mesh components like rifle/shotgun/sniper
            weaponType = 'leftGun';
            if (!this._leftGunMeshes) this._leftGunMeshes = gun.getComponents(MeshComponent);
            cachedMeshes = this._leftGunMeshes;
        } else if (gun === this.rightGun) {
            weaponType = 'rightGun';
            if (!this._rightGunMeshes) this._rightGunMeshes = gun.getComponents(MeshComponent);
            cachedMeshes = this._rightGunMeshes;
        } else {
            // Fallback for unknown guns - no state tracking
            cachedMeshes = gun.getComponents(MeshComponent);
            for (let i = 0; i < cachedMeshes.length; i++) cachedMeshes[i].active = active;
            return;
        }

        // GPU OPTIMIZATION: Only update if state actually changed
        if (this._meshStates[weaponType] !== active) {
            for (let i = 0; i < cachedMeshes.length; i++) cachedMeshes[i].active = active;
            this._meshStates[weaponType] = active;
        }
    }

    _handleAimAssistSwitch(switchAimAssist) {
        if (switchAimAssist && !this._aimAssistToggle) {
            this._aimAssistToggle = true;

            // Get current weapon and player progression
            const weapon = this._weaponSlots[this._currentSlotIndex] || 'default';
            const playerLevel = this.networkHandler ? this.networkHandler.level : 1;
            const playerPrestige = this.networkHandler ? this.networkHandler.prestige : 0;

            // Check which aim assist modes are available for current weapon
            const laserUnlocked = this.debugAllWeapons || ProgressionSystem.isLaserUnlocked(weapon, playerLevel, playerPrestige);
            const crosshairUnlocked = this.debugAllWeapons || ProgressionSystem.isCrosshairUnlocked(weapon, playerLevel, playerPrestige);

            // Cycle to next available state, skipping unavailable ones
            let nextState = (this._aimAssistState + 1) % 3;
            let attempts = 0;

            while (attempts < 3) {
                if (nextState === 0) {
                    // State 0 (none) is always available
                    break;
                } else if (nextState === 1 && laserUnlocked) {
                    // State 1 (laser) is available
                    break;
                } else if (nextState === 2 && crosshairUnlocked) {
                    // State 2 (crosshair) is available
                    break;
                } else {
                    // Skip to next state
                    nextState = (nextState + 1) % 3;
                    attempts++;
                }
            }

            this._aimAssistState = nextState;
            this._aimAssistManuallySet = true; // Mark as manually set by player
            this._updateAimAssistVisibility();

        } else if (!switchAimAssist) {
            this._aimAssistToggle = false;
        }
    }

    _updateMoveDirection(x, y) {
        const viewObj = (!this._isXRActive() && this._nonVrCamera) ? this._nonVrCamera : this.head;
        viewObj.getForwardWorld(this._headForward);
        viewObj.getRightWorld(this._headRight);
        this._headForward[1] = 0;
        this._headRight[1] = 0;
        vec3.normalize(this._headForward, this._headForward);
        vec3.normalize(this._headRight, this._headRight);
        vec3.set(this._moveDir, 0, 0, 0);
        vec3.scaleAndAdd(this._moveDir, this._moveDir, this._headForward, y);
        vec3.scaleAndAdd(this._moveDir, this._moveDir, this._headRight, x);
        vec3.normalize(this._moveDir, this._moveDir);
    }

    /**
     * For desktop (flat-screen) mode: compute fire origin and direction from head/camera.
     * Sets this.pos to head world position and this.pos2 to a point far along head forward.
     * Returns the normalized fire direction in the provided output vector.
     */
    _getDesktopFireVectors(outDirection, gunOrigin) {
        const viewObj = this._nonVrCamera || this.head;

        // 1. Get the spawn position from the gun's origin instead of the head
        if (gunOrigin) {
            gunOrigin.getPositionWorld(this.pos);
        } else {
            viewObj.getPositionWorld(this.pos);
        }

        // 2. Keep using the camera's forward vector for direction (perfect aim)
        viewObj.getForwardWorld(outDirection);

        // pos2 = pos + forward * 100 (target point for line/trail effects)
        vec3.scaleAndAdd(this.pos2, this.pos, outDirection, 100);

        // Apply a smaller offset if necessary to avoid clipping with the gun itself
        const offset = 0.05;
        vec3.scaleAndAdd(this.pos, this.pos, outDirection, offset);
    }

    _handleSprinting(sprintInput, dt) {
        // 1. Update the penalty timer
        if (this._depletionTimer > 0) {
            this._depletionTimer -= dt;
        }

        // 2. Check Movement & Exhaustion
        const isMoving = Math.abs(this._moveDir[0]) > 0.01 || Math.abs(this._moveDir[2]) > 0.01;
        
        // Lock sprint if exhausted until 30% recovered (ArenaWars penalty)
        if (this._sprintExhausted && this._currentEndurance < this.maxEndurance * 0.3) {
            sprintInput = false;
        } else {
            this._sprintExhausted = false;
        }

        // 3. THE STATE MACHINE (Strict Logic)
        // We only drain if: holding button AND moving AND have stamina AND not in penalty
        const canSprint = sprintInput && isMoving && this._currentEndurance > 0 && this._depletionTimer <= 0;

        if (canSprint) {
            this._isDraining = true;
            this._sprintTime += dt;

            // POLISH: Exponential Drain - stamina drains faster the longer you sprint, creating a risk-reward dynamic
            const drainMultiplier = Math.pow(1 + this._sprintTime, 0.5); 
            
            this._currentEndurance -= this.enduranceDrainRate * drainMultiplier * dt;

            // Hit Zero? Start the penalty
            if (this._currentEndurance <= 0) {
                this._currentEndurance = 0;
                this._sprintExhausted = true;
                this._depletionTimer = 2.0; 
                this._sprintTime = 0;
            }
        } else {
            // NOT SPRINTING
            this._isDraining = false;
            this._sprintTime = 0;

            // Only refill if not in the 2s penalty window
            if (this._depletionTimer <= 0 && this._currentEndurance < this.maxEndurance) {
                // POLISH: Tactical Recovery
                // 1.5x speed if standing still. Encourages taking cover.
                const recoveryMultiplier = isMoving ? 1.0 : 1.5;
                
                this._currentEndurance += this.enduranceRecoveryRate * recoveryMultiplier * dt;
                
                if (this._currentEndurance > this.maxEndurance) {
                    this._currentEndurance = this.maxEndurance;
                }
            }
        }

        // 4. PITCH PERFECT UI
        if (this.staminaBar) {
            const staminaRatio = Math.max(0, Math.min(this._currentEndurance / this.maxEndurance, 1));
            
            // Matches your exact XP bar logic
            const staminaScale = staminaRatio * 0.5;
            this._tempStaminaScale[0] = staminaScale;
            this._tempStaminaScale[1] = this.staminaBarScale[1];
            this._tempStaminaScale[2] = 0.5;
            this.staminaBar.setScalingLocal(this._tempStaminaScale);
        }

        return canSprint; // Return true only if we are actually burning stamina
    }

    _applySlopeAdjustment(desiredMove) {
        const slopeCos = vec3.dot(this._groundNormal, this._up);
        // APP TIME OPTIMIZATION: Use cached maxSlope calculation instead of Math.cos() every frame
        if (this._onGround && slopeCos < this._maxSlopeCos) {
            vec3.set(desiredMove, 0, 0, 0);
        } else if (this._onGround) {
            const dot = vec3.dot(desiredMove, this._groundNormal);
            vec3.scaleAndAdd(desiredMove, desiredMove, this._groundNormal, -dot);
        }
    }

    _updateMuzzleFlashes(dt) {
        // PERFORMANCE: Skip muzzle flash updates entirely - they're handled in weapon-sync with faster visual feedback
        // Keeping method for compatibility but reducing overhead by only updating active flashes
        const hasActiveFlash = this.leftGunMuzzleFlashIsActive || this.rightGunMuzzleFlashIsActive ||
            this.rifleGunMuzzleFlashIsActive || this.shotgunGunMuzzleFlashIsActive ||
            this.sniperGunMuzzleFlashIsActive;

        // dt-based idle throttle: only poll inactive flashes every ~0.4s (frame-rate independent)
        if (!hasActiveFlash) {
            this._muzzleFlashIdleTimer += dt;
            if (this._muzzleFlashIdleTimer < 0.4) return;
            this._muzzleFlashIdleTimer = 0;
        }

        if (this.leftGunMuzzleFlashIsActive) this._updateMuzzleFlash(dt, 'leftGunMuzzleFlash', '_leftGunMuzzleFlashScale', 'leftGunMuzzleFlashIsActive', 'leftGunMuzzleFlashTimer');
        if (this.rightGunMuzzleFlashIsActive) this._updateMuzzleFlash(dt, 'rightGunMuzzleFlash', '_rightGunMuzzleFlashScale', 'rightGunMuzzleFlashIsActive', 'rightGunMuzzleFlashTimer');
        if (this.rifleGunMuzzleFlashIsActive) this._updateMuzzleFlash(dt, 'rifleGunMuzzleFlash', '_rifleGunMuzzleFlashScale', 'rifleGunMuzzleFlashIsActive', 'rifleGunMuzzleFlashTimer');
        if (this.shotgunGunMuzzleFlashIsActive) this._updateMuzzleFlash(dt, 'shotgunGunMuzzleFlash', '_shotgunGunMuzzleFlashScale', 'shotgunGunMuzzleFlashIsActive', 'shotgunGunMuzzleFlashTimer');
        if (this.sniperGunMuzzleFlashIsActive) this._updateMuzzleFlash(dt, 'sniperGunMuzzleFlash', '_sniperGunMuzzleFlashScale', 'sniperGunMuzzleFlashIsActive', 'sniperGunMuzzleFlashTimer');
    }

    _updateMuzzleFlash(dt, flashProp, scaleProp, activeProp, timerProp) {
        if (this[activeProp]) {
            this[timerProp] += dt;
            const t = Math.min(this[timerProp] / this.MUZZLE_FLASH_DURATION, 1.0);

            if (t >= 1.0) {
                // PERF: Reuse pre-allocated zero scale instead of [0,0,0] array allocation
                this[flashProp].setScalingLocal(this._zeroScale);
                this[activeProp] = false;
                this[timerProp] = 0;
            } else {
                // PERF: Reuse pre-allocated temp scale vector instead of array allocation
                this._tempMuzzleScale[0] = this[scaleProp][0] * (1.0 - t);
                this._tempMuzzleScale[1] = this[scaleProp][1] * (1.0 - t);
                this._tempMuzzleScale[2] = this[scaleProp][2] * (1.0 - t);
                this[flashProp].setScalingLocal(this._tempMuzzleScale);
            }
        }
    }

    _lerp(a, b, t) {
        return a + (b - a) * t;
    }

    _handleShotgunFire(input) {
        if (this.networkHandler && this.networkHandler._isDead) return;
        // PERF: Use cached rifle-orient component instead of per-shot getComponent()
        const hand = this._shotgunRifleOrient ? this._shotgunRifleOrient.rifleHand : 'right';
        const isDesktop = !this._isXRActive();
        const triggerPressed = isDesktop
            ? input.leftTrigger?.pressed
            : ((hand === 'left' && input.leftTrigger?.pressed) || (hand === 'right' && input.rightTrigger?.pressed));
        if (triggerPressed && !this._shotgunTriggerPressed && this._shotgunFireTimer <= 0) {
            // Guard: Only fire if networkHandler is available
            if (!this.networkHandler) return;
            
            // [ANALYTICS HOOK]
            AnalyticsManager.instance.logShot();
            
            this._shotgunFireTimer = 0.5; // 500ms cooldown in seconds
            this._shotgunTriggerPressed = true;
            const pool = this._shotgunCurrentPool === 0 ? this.bulletPoolLeftGun : this.bulletPoolRightGun;
            if (this.shotgunShotSound) this.shotgunShotSound.play();
            if (isDesktop) {
                this._fireShotgunDesktop(this._physx.linearVelocity, pool, this._shotgunPoolIndex, hand);
            } else {
                this._fireShotgun(this.shotgunGunOrigin, this.shotgunGunTarget, this._physx.linearVelocity, pool, this._shotgunPoolIndex, hand);
            }

            // Trigger haptic feedback for shotgun
            if (this.networkHandler) {
                this.networkHandler.triggerWeaponHapticFeedback('shotgun', hand);
            }

            // PlayButton check
            if (isDesktop) {
                this._checkPlayButtonRaycastDesktop();
            } else {
                this._checkPlayButtonRaycast(this.shotgunGunOrigin, this.shotgunGunTarget);
            }
            this._shotgunPoolIndex += 8;
            if (this._shotgunPoolIndex >= pool.children.length) {
                this._shotgunCurrentPool = (this._shotgunCurrentPool + 1) % 2;
                this._shotgunPoolIndex = 0;
            }
        }
        if (!triggerPressed) {
            this._shotgunTriggerPressed = false;
        }
    }

    _handleLeftFire(input) {
        if (this.networkHandler && this.networkHandler._isDead) return;
        if (input.leftTrigger?.pressed) {
            if (!this.fireLeftGun && this._weaponSlots[0] === 'default') {
                // Guard: Only fire if networkHandler is available
                if (!this.networkHandler) return;
                
                // [ANALYTICS HOOK]
                AnalyticsManager.instance.logShot();
                
                if (this.leftGunSound) this.leftGunSound.play();
                this.fireLeftGun = true;
                this.leftGunMuzzleFlash.setScalingLocal(this._leftGunMuzzleFlashScale);
                this.leftGunMuzzleFlashIsActive = true;
                this.leftGunMuzzleFlashTimer = 0;
                this.MUZZLE_FLASH_DURATION = 0.025; // Fast akimbo flash

                // Desktop: fire from head; VR: fire from gun origin/target
                const isDesktop = !this._isXRActive();
                if (isDesktop) {
                    this._getDesktopFireVectors(this.directionLeftBullet, this.leftGunOrigin);
                } else {
                    this.leftGunOrigin.getPositionWorld(this.pos);
                    this.leftGunTarget.getPositionWorld(this.pos2);
                    vec3.subtract(this.directionLeftBullet, this.pos2, this.pos);
                    vec3.normalize(this.directionLeftBullet, this.directionLeftBullet);
                }

                // Add weapon sway for better feel
                this._applyWeaponSway(this.directionLeftBullet, 'pistol');

                vec3.scale(this.directionLeftBullet, this.directionLeftBullet, 750);
                vec3.add(this.directionLeftBullet, this.directionLeftBullet, this._physx.linearVelocity);
                this.bulletLeftGun = this.bulletPoolLeftGun.children[this.indexLeftGunPool];
                this.bulletLeftGun.setPositionWorld(this.pos);

                // NEW
                const kBulletLeft = this._kinBulletLeft[this.indexLeftGunPool];
                if (kBulletLeft) kBulletLeft.fire(this.directionLeftBullet);
                // Always use the currently active weapon for bullet type
                let weaponType = this._weaponSlots[this._currentSlotIndex] || 'default';
                const _t = this.networkHandler._elapsedMs || 0;
                this.networkHandler.fireBullet(this.pos, this.directionLeftBullet, _t, weaponType, undefined, 'left');
                if (this.networkHandler._botModeActive) {
                    this.networkHandler.handleBulletSpawn({
                        id: this.networkHandler.userId,
                        pos: this.pos,
                        linVel: this.directionLeftBullet,
                        t: _t,
                        weaponType: weaponType
                    });
                }

                // Trigger haptic feedback if available
                if (this.networkHandler) {
                    this.networkHandler.triggerWeaponHapticFeedback('default', 'left');
                }

                // PlayButton check
                if (isDesktop) {
                    this._checkPlayButtonRaycastDesktop();
                } else {
                    this._checkPlayButtonRaycast(this.leftGunOrigin, this.leftGunTarget);
                }
                this.indexLeftGunPool = (this.indexLeftGunPool + 1) % this.bulletPoolLeftGun.children.length;
                // PERF: dt-based cooldown replaces setTimeout closure allocation
                this._leftGunFireTimer = 0.2;
            }
        }
    }

    _handleRightFire(input) {
        if (this.networkHandler && this.networkHandler._isDead) return;
        if (input.rightTrigger?.pressed) {
            if (this._weaponSlots[0] === 'shotgun' && this.fireShotgun) return;
            if (this._weaponSlots[0] === 'rifle' && this.fireRifle) return;
            if (this._weaponSlots[0] === 'default' && this.fireRightGun) return;
            if (!this.fireRightGun && this._weaponSlots[0] === 'default') {
                // Guard: Only fire if networkHandler is available
                if (!this.networkHandler) return;
                
                // [ANALYTICS HOOK]
                AnalyticsManager.instance.logShot();
                
                if (this.rightGunSound) this.rightGunSound.play();
                this.fireRightGun = true;
                this.rightGunMuzzleFlash.setScalingLocal(this._rightGunMuzzleFlashScale);
                this.rightGunMuzzleFlashIsActive = true;
                this.rightGunMuzzleFlashTimer = 0;
                this.MUZZLE_FLASH_DURATION = 0.025; // Fast akimbo flash

                // Desktop: fire from head; VR: fire from gun origin/target
                const isDesktop = !this._isXRActive();
                if (isDesktop) {
                    this._getDesktopFireVectors(this.directionRightBullet, this.rightGunOrigin);
                } else {
                    this.rightGunOrigin.getPositionWorld(this.pos);
                    this.rightGunTarget.getPositionWorld(this.pos2);
                    vec3.subtract(this.directionRightBullet, this.pos2, this.pos);
                    vec3.normalize(this.directionRightBullet, this.directionRightBullet);
                }

                vec3.scale(this.directionRightBullet, this.directionRightBullet, 750);
                vec3.add(this.directionRightBullet, this.directionRightBullet, this._physx.linearVelocity);
                this.bulletRightGun = this.bulletPoolRightGun.children[this.indexRightGunPool];
                this.bulletRightGun.setPositionWorld(this.pos);
                const rightBulletComp = this.bulletRightGun._bulletCache || (this.bulletRightGun._bulletCache = this.bulletRightGun.getComponent('bullet'));
                if (rightBulletComp) rightBulletComp.active = true;
                const kBulletRight = this._kinBulletRight[this.indexRightGunPool];
                if (kBulletRight) kBulletRight.fire(this.directionRightBullet);
                // Always use the currently active weapon for bullet type
                let weaponType = this._weaponSlots[this._currentSlotIndex] || 'default';
                const _t = this.networkHandler._elapsedMs || 0;
                this.networkHandler.fireBullet(this.pos, this.directionRightBullet, _t, weaponType, undefined, 'right');
                if (this.networkHandler._botModeActive) {
                    this.networkHandler.handleBulletSpawn({
                        id: this.networkHandler.userId,
                        pos: this.pos,
                        linVel: this.directionRightBullet,
                        t: _t,
                        weaponType: weaponType
                    });
                }
                // Trigger haptic feedback if available
                if (this.networkHandler) {
                    this.networkHandler.triggerWeaponHapticFeedback('default', 'right');
                }

                // PlayButton check
                if (isDesktop) {
                    this._checkPlayButtonRaycastDesktop();
                } else {
                    this._checkPlayButtonRaycast(this.rightGunOrigin, this.rightGunTarget);
                }
                this.indexRightGunPool = (this.indexRightGunPool + 1) % this.bulletPoolRightGun.children.length;
                // PERF: dt-based cooldown replaces setTimeout closure allocation
                this._rightGunFireTimer = 0.2;
            }
        }
    }

    _handleRifleFire(input) {
        if (this.networkHandler && this.networkHandler._isDead) return;
        const isDesktop = !this._isXRActive();
        // PERF: Use cached rifle-orient component instead of per-shot getComponent()
        const hand = this._rifleRifleOrient ? this._rifleRifleOrient.rifleHand : 'right';

        // Desktop: left click fires (use leftTrigger); VR: hand-based trigger
        const shouldFire = isDesktop
            ? (input.leftTrigger?.pressed && !this.fireRifle)
            : ((hand === 'left' && input.leftTrigger?.pressed && !this.fireRifle) ||
                (hand === 'right' && input.rightTrigger?.pressed && !this.fireRifle));
        const fireHand = isDesktop ? 'right' : hand;
        const directionBullet = (fireHand === 'left') ? this.directionLeftBullet : this.directionRightBullet;

        if (shouldFire) {
            // Guard: Only fire if networkHandler is available
            if (!this.networkHandler) return;
            
            // [ANALYTICS HOOK]
            AnalyticsManager.instance.logShot();
            
            this.fireRifle = true;
            if (this.rifleShotSound) this.rifleShotSound.play();
            this.rifleGunMuzzleFlash.setScalingLocal(this._rifleGunMuzzleFlashScale);
            this.rifleGunMuzzleFlashIsActive = true;
            this.rifleGunMuzzleFlashTimer = 0;

            if (isDesktop) {
                this._getDesktopFireVectors(directionBullet, this.rifleGunOrigin);
            } else {
                this.rifleGunOrigin.getPositionWorld(this.pos);
                this.rifleGunTarget.getPositionWorld(this.pos2);
                vec3.subtract(directionBullet, this.pos2, this.pos);
                vec3.normalize(directionBullet, directionBullet);
            }

            // Add weapon sway for rifle
            this._applyWeaponSway(directionBullet, 'rifle');

            vec3.scale(directionBullet, directionBullet, 750);
            vec3.add(directionBullet, directionBullet, this._physx.linearVelocity);
            const isLeftPool = (this.rifleBulletIndex % 2 === 0);
            const pool = isLeftPool ? this.bulletPoolLeftGun : this.bulletPoolRightGun;
            const index = Math.floor(this.rifleBulletIndex / 2) % pool.children.length;
            const bullet = pool.children[index];
            bullet.setPositionWorld(this.pos);
            const kinArr = isLeftPool ? this._kinBulletLeft : this._kinBulletRight;
            const kBulletRifle = kinArr[index];
            if (kBulletRifle) kBulletRifle.fire(directionBullet);
            let weaponType = this._weaponSlots[this._currentSlotIndex] || 'rifle';
            const _t = this.networkHandler._elapsedMs || 0;
            this.networkHandler.fireBullet(this.pos, directionBullet, _t, weaponType, undefined, fireHand);
            if (this.networkHandler._botModeActive) {
                this.networkHandler.handleBulletSpawn({
                    id: this.networkHandler.userId,
                    pos: this.pos,
                    linVel: directionBullet,
                    t: _t,
                    weaponType: weaponType
                });
            }

            if (this.networkHandler) {
                this.networkHandler.triggerWeaponHapticFeedback('rifle', fireHand);
            }

            if (isDesktop) {
                this._checkPlayButtonRaycastDesktop();
            } else {
                this._checkPlayButtonRaycast(this.rifleGunOrigin, this.rifleGunTarget);
            }
            this.rifleBulletIndex++;
            // PERF: dt-based cooldown replaces setTimeout closure allocation
            this._rifleFireTimer = 0.1;
        }
    }

    _handleSniperFire(input) {
        if (this.networkHandler && this.networkHandler._isDead) return;
        const isDesktop = !this._isXRActive();
        // PERF: Use cached rifle-orient + audio components instead of per-shot getComponent()
        const hand = this._sniperRifleOrient ? this._sniperRifleOrient.rifleHand : 'right';
        const triggerPressed = isDesktop
            ? input.leftTrigger?.pressed
            : ((hand === 'left' && input.leftTrigger?.pressed) || (hand === 'right' && input.rightTrigger?.pressed));
        if (triggerPressed && !this._sniperTriggerPressed && this._sniperFireTimer <= 0) {
            // Guard: Only fire if networkHandler is available
            if (!this.networkHandler) return;
            
            // [ANALYTICS HOOK]
            AnalyticsManager.instance.logShot();
            
            this._sniperFireTimer = 0.8; // 800ms cooldown in seconds
            this._sniperTriggerPressed = true;
            if (this._sniperShotSound) {
                this._sniperShotSound.play();
            }
            // --- LOCAL CLIENT MUZZLE FLASH (fast, not synced) ---
            if (this.sniperGunMuzzleFlash && this._sniperGunMuzzleFlashScale) {
                this.sniperGunMuzzleFlash.setScalingLocal(this._sniperGunMuzzleFlashScale);
                this.sniperGunMuzzleFlashIsActive = true;
                this.sniperGunMuzzleFlashTimer = 0;
                this.MUZZLE_FLASH_DURATION = 0.025;
            }

            if (isDesktop) {
                this._getDesktopFireVectors(this._bulletDirection, this.sniperGunOrigin);
            } else {
                this.sniperGunOrigin.getPositionWorld(this.pos);
                this.sniperGunTarget.getPositionWorld(this.pos2);
                vec3.subtract(this._bulletDirection, this.pos2, this.pos);
                vec3.normalize(this._bulletDirection, this._bulletDirection);
            }
            vec3.scale(this._bulletDirection, this._bulletDirection, 1500); // faster bullet
            vec3.add(this._bulletDirection, this._bulletDirection, this._physx.linearVelocity);
            const isLeftPool = (this.sniperBulletIndex % 2 === 0);
            const pool = isLeftPool ? this.bulletPoolLeftGun : this.bulletPoolRightGun;
            const index = Math.floor(this.sniperBulletIndex / 2) % pool.children.length;
            const bullet = pool.children[index];
            bullet.setPositionWorld(this.pos);
            const kinArrSniper = isLeftPool ? this._kinBulletLeft : this._kinBulletRight;
            const kBulletSniper = kinArrSniper[index];
            if (kBulletSniper) kBulletSniper.fire(this._bulletDirection);
            let weaponType = 'sniper';
            const _t = this.networkHandler._elapsedMs || 0;
            this.networkHandler.fireBullet(this.pos, this._bulletDirection, _t, weaponType, undefined, hand);
            if (this.networkHandler._botModeActive) {
                this.networkHandler.handleBulletSpawn({
                    id: this.networkHandler.userId,
                    pos: this.pos,
                    linVel: this._bulletDirection,
                    t: _t,
                    weaponType: weaponType
                });
            }
            if (this.networkHandler) {
                this.networkHandler.triggerWeaponHapticFeedback('sniper', hand);
            }
            if (isDesktop) {
                this._checkPlayButtonRaycastDesktop();
            } else {
                this._checkPlayButtonRaycast(this.sniperGunOrigin, this.sniperGunTarget);
            }
            this.sniperBulletIndex++;
        }
        if (!triggerPressed) {
            this._sniperTriggerPressed = false;
        }
    }

    _handleJump(dt, jumpPressed, velocity) {
        if (this._onGround) {
            this._coyoteTimer = this.coyoteTime;
            this._jumpCount = 0;
        } else {
            this._coyoteTimer -= dt;
        }
        if (this._jumpBufferTimer > 0) this._jumpBufferTimer -= dt;
        if (jumpPressed && !this._wasJumpPressed) {
            this._jumpBufferTimer = this.jumpBufferTime;
        }
        const canJump = this._jumpBufferTimer > 0 &&
            (this._onGround || this._coyoteTimer > 0 || this._jumpCount < this.maxJumpCount);
        if (canJump) {
            velocity[1] = this.jumpForce;
            this._jumpCount++;
            this._jumpBufferTimer = 0;
            this._coyoteTimer = 0;
            this._onGround = false;
            this._lastJumpTime = this._frameTime;
        }
        this._wasJumpPressed = jumpPressed;
    }

    _checkGround() {
        // APP TIME OPTIMIZATION: Use cached frame time instead of Date.now()
        if (this._frameTime - this._lastJumpTime < this._groundGraceDelay) {
            this._onGround = false;
            return;
        }

        // Performance: Only check ground every 2 frames for non-critical ground detection
        if (this._groundCheckCounter++ % 2 !== 0 && this._onGround) {
            return; // Skip check if we were on ground last frame
        }

        this._onGround = false;
        this.feet.getPositionWorld(this._origin);

        // Performance: Cache raycast results (threshold in ms, matches ground check interval)
        if (!this._cachedGroundHit || this._frameTime - this._lastGroundCheck > 100) {
            this._cachedGroundHit = this.engine.physics.rayCast(this._origin, this._downVector, 1 << 0, 0.2);
            this._lastGroundCheck = this._frameTime;
        }

        const hit = this._cachedGroundHit;
        if (hit.hitCount > 0) {
            if (hit.objects[0].name === "Map" || hit.objects[1]?.name === "Map" || hit.objects[2]?.name === "Map" || hit.objects[3]?.name === "Map") {
                this._onGround = true;
                this._jumpCount = 0;
            }
        }
        if (!this._onGround) {
            vec3.set(this._groundNormal, 0, 1, 0);
        }
    }

    _checkPickups() {
        // PERF: Reuse array by truncating length instead of creating new [] each call
        let overlapCount = 0;
        const overlaps = this.playerCollision.queryOverlaps();

        // PERFORMANCE: Process with early exit and limit checks to first 5 overlaps
        const maxChecks = Math.min(overlaps.length, 5);
        for (let i = 0; i < maxChecks; i++) {
            const collision = overlaps[i];
            const parent = collision.object.parent;
            if (parent) {
                const parentName = parent.name;
                if (this._validPickupNames.has(parentName)) {
                    if (overlapCount >= this._overlappingPickups.length) {
                        this._overlappingPickups.push({
                            name: parentName,
                            object: parent
                        });
                    } else {
                        this._overlappingPickups[overlapCount].name = parentName;
                        this._overlappingPickups[overlapCount].object = parent;
                    }
                    overlapCount++;
                }
            }
        }
        this._overlappingPickups.length = overlapCount;
    }

    _fireShotgun(origin, target, velocityBuffer, pool, poolIndexStart, hand, numPellets = 8) {
        origin.getPositionWorld(this.pos);
        target.getPositionWorld(this.pos2);
        const _t = this.networkHandler._elapsedMs || 0;
        for (let i = 0; i < numPellets; i++) {
            const bullet = pool.children[(poolIndexStart + i) % pool.children.length];
            bullet.setPositionWorld(this.pos);
            // Normalize direction before spread so offset magnitude is consistent
            vec3.subtract(this._tempDirection, this.pos2, this.pos);
            vec3.normalize(this._tempDirection, this._tempDirection);
            this._tempDirection[0] += (Math.random() * 0.12) - 0.06;
            this._tempDirection[1] += (Math.random() * 0.12) - 0.06;
            this._tempDirection[2] += (Math.random() * 0.12) - 0.06;
            vec3.normalize(this._tempDirection, this._tempDirection);
            vec3.scale(this._tempDirection, this._tempDirection, 750);
            vec3.add(this._tempDirection, this._tempDirection, velocityBuffer);
            // Performance: cache physics components
            if (!bullet._kinBulletCache) {
                bullet._kinBulletCache = bullet.getComponent(KinematicBullet);
            }
            const kBulletSG = bullet._kinBulletCache;
            if (kBulletSG) kBulletSG.fire(this._tempDirection);
            // Always use the currently active weapon for bullet type
            let weaponType = this._weaponSlots[this._currentSlotIndex] || 'shotgun';
            this.networkHandler.fireBullet(this.pos, this._tempDirection, _t, weaponType, undefined, hand);
            if (this.networkHandler._botModeActive) {
                this.networkHandler.handleBulletSpawn({
                    id: this.networkHandler.userId,
                    pos: this.pos,
                    linVel: this._tempDirection,
                    t: _t,
                    weaponType: weaponType
                });
            }
        }
        this.shotgunGunMuzzleFlash.setScalingLocal(this._shotgunGunMuzzleFlashScale);
        this.shotgunGunMuzzleFlashIsActive = true;
        this.shotgunGunMuzzleFlashTimer = 0;
    }

    /** Desktop shotgun fire: pellets originate from camera position along camera forward */
    _fireShotgunDesktop(velocityBuffer, pool, poolIndexStart, hand, numPellets = 8) {
        const viewObj = this._nonVrCamera || this.head;
        this.shotgunGunOrigin.getPositionWorld(this.pos);
        const _tD = this.networkHandler._elapsedMs || 0;
        for (let i = 0; i < numPellets; i++) {
            const bullet = pool.children[(poolIndexStart + i) % pool.children.length];
            bullet.setPositionWorld(this.pos);
            // Get fresh unit-length forward each pellet so spread offsets are significant
            viewObj.getForwardWorld(this._tempDirection);
            this._tempDirection[0] += (Math.random() * 0.12) - 0.06;
            this._tempDirection[1] += (Math.random() * 0.12) - 0.06;
            this._tempDirection[2] += (Math.random() * 0.12) - 0.06;
            vec3.normalize(this._tempDirection, this._tempDirection);
            vec3.scale(this._tempDirection, this._tempDirection, 750);
            vec3.add(this._tempDirection, this._tempDirection, velocityBuffer);
            if (!bullet._kinBulletCache) {
                bullet._kinBulletCache = bullet.getComponent(KinematicBullet);
            }
            const kBulletSGD = bullet._kinBulletCache;
            if (kBulletSGD) kBulletSGD.fire(this._tempDirection);
            let weaponType = this._weaponSlots[this._currentSlotIndex] || 'shotgun';
            this.networkHandler.fireBullet(this.pos, this._tempDirection, _tD, weaponType, undefined, hand);
            if (this.networkHandler._botModeActive) {
                this.networkHandler.handleBulletSpawn({
                    id: this.networkHandler.userId,
                    pos: this.pos,
                    linVel: this._tempDirection,
                    t: _tD,
                    weaponType: weaponType
                });
            }
        }
        this.shotgunGunMuzzleFlash.setScalingLocal(this._shotgunGunMuzzleFlashScale);
        this.shotgunGunMuzzleFlashIsActive = true;
        this.shotgunGunMuzzleFlashTimer = 0;
    }

    /** Desktop PlayButton raycast: fires from camera position along camera forward direction */
    _checkPlayButtonRaycastDesktop() {
        const viewObj = this._nonVrCamera || this.head;
        viewObj.getPositionWorld(this.pos);
        viewObj.getForwardWorld(this._raycastDirection);
        // PhysX raycast for PlayButton (group 0)
        const hit = this.engine.physics.rayCast(this.pos, this._raycastDirection, 1 << 0, 100);
        if (hit.hitCount > 0) {
            for (let i = 0; i < hit.hitCount; i++) {
                const obj = hit.objects[i];
                if (obj.name === 'PlayButton') {
                    if (obj.parent && obj.parent.getComponent) {
                        const lobbyHandler = obj.parent.getComponent('lobby-handler');
                        if (lobbyHandler && typeof lobbyHandler.startGame === 'function') {
                            lobbyHandler.startGame();
                        }
                    }
                    obj.active = false;
                    this._onPlayButtonHit(obj);
                    break;
                } else if (obj.name === 'BuyButton') {
                    this.shopBuyButton.onPress();
                    break;
                } else if (obj.name === 'CoinsButton') {
                    // link to coin page in new tab
                    window.open('https://heyvr.io/market', '_blank', 'noopener,noreferrer');
                }
            }
        }
        // Scene raycast for CollisionComponent objects like the video ad (group 2)
        const sceneHit = this.engine.scene.rayCast(this.pos, this._raycastDirection, 1 << 2, 100);
        if (sceneHit.hitCount > 0) {
            for (let i = 0; i < sceneHit.hitCount; i++) {
                const obj = sceneHit.objects[i];
                const ct = obj.getComponent(CursorTarget);
                if (ct) {
                    ct.onClick.notify(obj, null);
                    break;
                }
            }
        }
    }

    _updateAimAssistVisibility() {
        // If debug laser toggle is enabled, skip normal aim assist visibility logic
        if (this.debugLaserToggle) return;

        // Get current player level and prestige from network handler
        const playerLevel = this.networkHandler ? this.networkHandler.level : 1;
        const playerPrestige = this.networkHandler ? this.networkHandler.prestige : 0;
        const isDesktop = !this._isXRActive();

        // Only enable the relevant laser/crosshair for the current weapon and aim assist state
        // First, turn off all lasers/crosshairs
        this.laserLeftGun.active = false;
        this.laserRightGun.active = false;
        this.crosshairLeftGun.active = false;
        this.crosshairRightGun.active = false;
        this.laserRifleGun.active = false;
        this.crosshairRifleGun.active = false;
        this.shotgunLaser.active = false;
        this.shotgunCrosshair.active = false;
        this.sniperLaser.active = false;
        this.crosshairSniperGun.active = false;

        // if is weapon in switch process return 
        if (this._isSwitchingWeapon) {
            // Still update desktop crosshair even during weapon switch
            if (isDesktop) this._updateDesktopCrosshair(playerLevel, playerPrestige);
            return;
        }

        // Now, enable only the dedicated laser/crosshair for the current weapon
        const weapon = this._weaponSlots[this._currentSlotIndex] || 'default';

        if (weapon === 'default') {
            // Check if akimbo laser/crosshair is unlocked
            const laserUnlocked = this.debugAllWeapons || ProgressionSystem.isLaserUnlocked('default', playerLevel, playerPrestige);
            const crosshairUnlocked = this.debugAllWeapons || ProgressionSystem.isCrosshairUnlocked('default', playerLevel, playerPrestige);

            if (this._aimAssistState === 1 && laserUnlocked) {
                this.laserLeftGun.active = true;
                this.laserRightGun.active = true;
            } else if (this._aimAssistState === 2 && crosshairUnlocked && !isDesktop) {
                // 3D crosshairs only in VR; desktop uses the HTML crosshair at screen center
                this.crosshairLeftGun.active = true;
                this.crosshairRightGun.active = true;
            }
        } else if (weapon === 'rifle') {
            const laserUnlocked = this.debugAllWeapons || ProgressionSystem.isLaserUnlocked('rifle', playerLevel, playerPrestige);
            const crosshairUnlocked = this.debugAllWeapons || ProgressionSystem.isCrosshairUnlocked('rifle', playerLevel, playerPrestige);

            if (this._aimAssistState === 1 && laserUnlocked) {
                this.laserRifleGun.active = true;
            } else if (this._aimAssistState === 2 && crosshairUnlocked && !isDesktop) {
                this.crosshairRifleGun.active = true;
            }
        } else if (weapon === 'shotgun') {
            const laserUnlocked = this.debugAllWeapons || ProgressionSystem.isLaserUnlocked('shotgun', playerLevel, playerPrestige);
            const crosshairUnlocked = this.debugAllWeapons || ProgressionSystem.isCrosshairUnlocked('shotgun', playerLevel, playerPrestige);

            if (this._aimAssistState === 1 && laserUnlocked) {
                this.shotgunLaser.active = true;
            } else if (this._aimAssistState === 2 && crosshairUnlocked && !isDesktop) {
                this.shotgunCrosshair.active = true;
            }
        } else if (weapon === 'sniper') {
            const laserUnlocked = this.debugAllWeapons || ProgressionSystem.isLaserUnlocked('sniper', playerLevel, playerPrestige);
            const crosshairUnlocked = this.debugAllWeapons || ProgressionSystem.isCrosshairUnlocked('sniper', playerLevel, playerPrestige);

            if (this._aimAssistState === 1 && laserUnlocked && this.sniperLaser) {
                this.sniperLaser.active = true;
            } else if (this._aimAssistState === 2 && crosshairUnlocked && this.crosshairSniperGun && !isDesktop) {
                this.crosshairSniperGun.active = true;
            }
        }

        // Update the HTML desktop crosshair to reflect current upgrade state and colors
        if (isDesktop) this._updateDesktopCrosshair(playerLevel, playerPrestige);
    }

    /**
     * Update desktop crosshair visibility based on pointer lock, XR state, and progression.
     * Hides crosshair when nothing is unlocked or aim assist is off.
     */
    _updateDesktopCrosshairVisibility() {
        const crosshairEl = this._crosshairEl;
        if (!crosshairEl) return;

        if (!this._isFlatLookActive()) {
            crosshairEl.style.display = 'none';
            return;
        }

        const playerLevel = this.networkHandler ? this.networkHandler.level : 1;
        const playerPrestige = this.networkHandler ? this.networkHandler.prestige : 0;
        const weapon = this._weaponSlots[this._currentSlotIndex] || 'default';
        const laserUnlocked = this.debugAllWeapons || ProgressionSystem.isLaserUnlocked(weapon, playerLevel, playerPrestige);
        const crosshairUnlocked = this.debugAllWeapons || ProgressionSystem.isCrosshairUnlocked(weapon, playerLevel, playerPrestige);

        const aimActive = (this._aimAssistState === 1 && laserUnlocked) ||
            (this._aimAssistState === 2 && crosshairUnlocked);

        crosshairEl.style.display = aimActive ? 'block' : 'none';
    }

    /**
     * Update the HTML desktop crosshair color to match the current upgrade/prestige state.
     * In desktop mode, the HTML crosshair at screen center replaces VR's 3D crosshair objects.
     * When a laser or crosshair upgrade is active, the crosshair color reflects the prestige laser color.
     */
    _updateDesktopCrosshair(playerLevel, playerPrestige) {
        const crosshairEl = this._crosshairEl;
        if (!crosshairEl) return;

        const weapon = this._weaponSlots[this._currentSlotIndex] || 'default';
        const laserUnlocked = this.debugAllWeapons || ProgressionSystem.isLaserUnlocked(weapon, playerLevel, playerPrestige);
        const crosshairUnlocked = this.debugAllWeapons || ProgressionSystem.isCrosshairUnlocked(weapon, playerLevel, playerPrestige);

        const aimActive = (this._aimAssistState === 1 && laserUnlocked) ||
            (this._aimAssistState === 2 && crosshairUnlocked);

        // Control visibility: hide when aim assist is off or nothing unlocked
        crosshairEl.style.display = (this._isFlatLookActive() && aimActive)
            ? 'block' : 'none';

        if (aimActive) {
            const color = this._getEquippedLaserColor();
            const r = Math.round(color[0] * 255);
            const g = Math.round(color[1] * 255);
            const b = Math.round(color[2] * 255);
            const cssColor = `rgba(${r}, ${g}, ${b}, 0.9)`;
            crosshairEl.style.setProperty('--crosshair-color', cssColor);
            crosshairEl.style.setProperty('--dot-color', cssColor);
        } else {
            this._resetDesktopCrosshair();
        }
    }

    /** Reset the HTML desktop crosshair to default white/cyan colors */
    _resetDesktopCrosshair() {
        const crosshairEl = this._crosshairEl;
        if (!crosshairEl) return;
        crosshairEl.style.setProperty('--crosshair-color', 'rgba(255, 255, 255, 0.85)');
        crosshairEl.style.setProperty('--dot-color', 'rgba(0, 255, 255, 0.9)');
    }

    _hideAllMuzzleFlashes() {
        // PERF: Reuse pre-allocated zero scale instead of [0,0,0] array allocations
        const z = this._zeroScale;
        if (this.leftGunMuzzleFlash) this.leftGunMuzzleFlash.setScalingLocal(z);
        if (this.rightGunMuzzleFlash) this.rightGunMuzzleFlash.setScalingLocal(z);
        if (this.rifleGunMuzzleFlash) this.rifleGunMuzzleFlash.setScalingLocal(z);
        if (this.shotgunGunMuzzleFlash) this.shotgunGunMuzzleFlash.setScalingLocal(z);
        if (this.sniperGunMuzzleFlash) this.sniperGunMuzzleFlash.setScalingLocal(z);
    }

    _updateWeaponMeshes() {
        // Hide all muzzle flashes when switching weapons
        this._hideAllMuzzleFlashes();
        // Enable only the current weapon's meshes
        const weapon = this._weaponSlots[this._currentSlotIndex];
        this._setMeshesActive(this.leftGun, weapon === 'default');
        this._setMeshesActive(this.rightGun, weapon === 'default');
        this._setMeshesActive(this.rifleGun, weapon === 'rifle');
        this._setMeshesActive(this.shotgunGun, weapon === 'shotgun');
        this._setMeshesActive(this.sniperGun, weapon === 'sniper');
    }

    _addWeaponToSlot(weapon) {
        // Disable all aim assists when picking up a new weapon
        this._updateAimAssistVisibility();
        // If weapon already in slots, do nothing
        if (this._weaponSlots.includes(weapon)) return;

        // Define preferred slot for each weapon type for consistent ordering
        const weaponSlotMap = {
            'default': 0,  // Akimbo always in slot 0
            'rifle': 1,    // Rifle in slot 1
            'shotgun': 2,  // Shotgun in slot 2
            'sniper': 3    // Sniper in slot 3
        };

        // Try to place weapon in its preferred slot first
        const preferredSlot = weaponSlotMap[weapon] || 0;
        if (!this._weaponSlots[preferredSlot]) {
            this._weaponSlots[preferredSlot] = weapon;
            this._currentSlotIndex = preferredSlot;
            this._updateWeaponMeshes();
            this._applyOptimalAimAssistForWeapon();
            this._updateAimAssistVisibility();
            this._updateIconHighlights();
            this._showPickupMessage(weapon);
            if (this.unlockSound) this.unlockSound.play();
            return;
        }

        // If preferred slot is taken, find any empty slot
        for (let i = 0; i < 4; ++i) {
            if (!this._weaponSlots[i]) {
                this._weaponSlots[i] = weapon;
                this._currentSlotIndex = i;
                this._updateWeaponMeshes();
                this._applyOptimalAimAssistForWeapon();
                this._updateAimAssistVisibility();
                this._updateIconHighlights();
                this._showPickupMessage(weapon);
                if (this.unlockSound) this.unlockSound.play();
                return;
            }
        }

        // All slots full: replace current
        this._weaponSlots[this._currentSlotIndex] = weapon;
        this._updateWeaponMeshes();
        this._applyOptimalAimAssistForWeapon();
        this._updateAimAssistVisibility();
        this._updateIconHighlights();
        this._showPickupMessage(weapon);
        if (this.unlockSound) this.unlockSound.play();
    }

    _showPickupMessage(weapon) {
        if (!this.gameInfoText) return;
        const name = weapon.charAt(0).toUpperCase() + weapon.slice(1);
        this.gameInfoText.text = `Picked up: ${name}`;
        this.gameInfoText.active = true;
        // QUEST 2 OPTIMIZATION: dt-based timer replaces setTimeout closure
        this._pickupMessageTimer = 2.0;
        this._pickupMessageName = name;
    }

    /**
     * Check if crosshair is toggled on in the user settings
     * @param {string} weapon - weapon type
     * @returns {boolean}
     */
    _isCrosshairSettingEnabled(weapon) {
        let localSettings = {};
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localSettings = JSON.parse(window.localStorage.getItem('arena.settings') || '{}');
            }
        } catch (e) {}
        const key = 'crosshair_' + (weapon === 'default' ? 'default' : weapon);
        return localSettings[key] !== undefined ? !!localSettings[key] : false;
    }

    /**
     * Get the active laser color based on player preference and prestige unlocks
     * @returns {Float32Array}
     */
    _getEquippedLaserColor() {
        let localSettings = {};
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localSettings = JSON.parse(window.localStorage.getItem('arena.settings') || '{}');
            }
        } catch (e) {}
        const prefColor = localSettings.laserColor || 'cyan';

        const prestige = this.networkHandler ? this.networkHandler.prestige : 0;
        const level = this.networkHandler ? this.networkHandler.level : 1;
        const purchasedItems = this.networkHandler ? this.networkHandler.purchasedItems : null;

        let activeColorKey = 0; // Default cyan
        if (prefColor === 'blue' && ProgressionSystem.isLaserColorUnlocked('blue', level, prestige, purchasedItems)) {
            activeColorKey = 1;
        } else if (prefColor === 'purple' && ProgressionSystem.isLaserColorUnlocked('purple', level, prestige, purchasedItems)) {
            activeColorKey = 2;
        } else if (prefColor === 'gold' && ProgressionSystem.isLaserColorUnlocked('gold', level, prestige, purchasedItems)) {
            activeColorKey = 3;
        }
        return ProgressionSystem.getLaserColor(activeColorKey);
    }

    /** Helper to check if laser is unlocked and equipped */
    _isLaserEquipped(weaponType, playerLevel, playerPrestige) {
        const purchasedItems = this.networkHandler ? this.networkHandler.purchasedItems : null;
        return this.debugAllWeapons || ProgressionSystem.isLaserUnlocked(weaponType, playerLevel, playerPrestige, purchasedItems);
    }

    /** Helper to check if crosshair is unlocked and toggled on by settings */
    _isCrosshairEquipped(weaponType, playerLevel, playerPrestige) {
        const purchasedItems = this.networkHandler ? this.networkHandler.purchasedItems : null;
        const unlocked = this.debugAllWeapons || ProgressionSystem.isCrosshairUnlocked(weaponType, playerLevel, playerPrestige, purchasedItems);
        return unlocked && this._isCrosshairSettingEnabled(weaponType);
    }

    /**
     * Set up weapon loadout based on the player's saved preference
     * Called once on respawn/reset to avoid multiple overrides
     */
    _setupOptimalLoadout() {
        // Prevent multiple calls during the same respawn cycle
        if (this._loadoutSetupInProgress) {
            return;
        }
        this._loadoutSetupInProgress = true;

        // 1. Fetch current strict loadout from ArenaHandler
        let primary = 'default';
        let secondary = null;

        if (window.arenaHandler && window.arenaHandler.loadout) {
            const loadout = window.arenaHandler.loadout;
            if (loadout.primary && loadout.primary.id && loadout.primary.id !== 'none') {
                primary = loadout.primary.id;
            }
            if (loadout.secondary && loadout.secondary.id && loadout.secondary.id !== 'none') {
                secondary = loadout.secondary.id;
            }
        }

        // 2. Set strict dual loadout slots
        this._weaponSlots = [primary, secondary];
        this._currentSlotIndex = 0;

        // Set optimal aim assist state based on unlocks for current weapon
        this._aimAssistState = this._getOptimalAimAssistState(playerLevel, playerPrestige);

        // QUEST 2 OPTIMIZATION: dt-based timer replaces setTimeout closure
        this._loadoutResetTimer = 1.0;
    }

    /**
     * Determine optimal aim assist state based on progression unlocks
     * Prefers laser sight over crosshair if both are available
     * @param {number} playerLevel - Current player level
     * @param {number} playerPrestige - Current player prestige
     * @returns {number} Optimal aim assist state (0=none, 1=laser, 2=crosshair)
     */
    _getOptimalAimAssistState(playerLevel, playerPrestige) {
        const currentWeapon = this._weaponSlots[this._currentSlotIndex] || 'default';
        const purchasedItems = this.networkHandler ? this.networkHandler.purchasedItems : null;

        // Check what's unlocked for the current weapon
        const laserUnlocked = this.debugAllWeapons || ProgressionSystem.isLaserUnlocked(currentWeapon, playerLevel, playerPrestige, purchasedItems);
        const crosshairUnlocked = this.debugAllWeapons || ProgressionSystem.isCrosshairUnlocked(currentWeapon, playerLevel, playerPrestige, purchasedItems);

        // Prefer laser sight over crosshair if both available
        if (laserUnlocked) {
            return 1; // Laser state
        } else if (crosshairUnlocked) {
            return 2; // Crosshair state
        } else {
            return 0; // No aim assist
        }
    }

    /**
     * Apply optimal aim assist state for current weapon if not manually set by player
     * Called during weapon switches to automatically enable best available aim assist
     */
    _applyOptimalAimAssistForWeapon() {
        // Only apply optimal state if player hasn't manually customized their preference
        if (this._aimAssistManuallySet) {
            return;
        }

        const playerLevel = this.networkHandler ? this.networkHandler.level : 1;
        const playerPrestige = this.networkHandler ? this.networkHandler.prestige : 0;
        const optimalState = this._getOptimalAimAssistState(playerLevel, playerPrestige);

        if (this._aimAssistState !== optimalState) {
            this._aimAssistState = optimalState;
        }
    }

    resetWeapons() {
        // Reset manual aim assist flag so optimal state is applied
        this._aimAssistManuallySet = false;

        // Set up optimal loadout based on current progression unlocks
        this._setupOptimalLoadout();

        // Reset weapon meshes to show current weapons
        this._updateWeaponMeshes();

        // Reset icon highlights to show current layout
        this._updateIconHighlights();

        // Hide all muzzle flashes
        this._hideAllMuzzleFlashes();

        // Reset aim assist visibility for current weapon
        this._updateAimAssistVisibility();

    }

    /**
     * Update progression-based unlocks when player levels up or prestiges
     * @param {number} newLevel - New player level
     * @param {number} newPrestige - New prestige level (optional)
     */
    updateProgressionUnlocks(newLevel, newPrestige = null) {
        const playerPrestige = newPrestige !== null ? newPrestige : (this.networkHandler ? this.networkHandler.prestige : 0);
        const prestigeName = ProgressionSystem.getPrestigeName(playerPrestige);

        // Update weapon slots to include newly permanent weapons
        // PERF: Check against live _weaponSlots directly instead of spread-copy allocation
        const slots = this._weaponSlots;

        // Check for newly unlocked permanent weapons and add to their preferred slots
        const permanentWeapons = [
            { weapon: 'rifle', slot: 1 },
            { weapon: 'shotgun', slot: 2 },
            { weapon: 'sniper', slot: 3 }
        ];

        for (const { weapon, slot } of permanentWeapons) {
            if (ProgressionSystem.isWeaponPermanent(weapon, newLevel, playerPrestige) && !slots.includes(weapon)) {
                // Try to add to preferred slot first
                if (!slots[slot]) {
                    slots[slot] = weapon;
                } else {
                    // Find any available slot if preferred is taken
                    for (let i = 0; i < 4; i++) {
                        if (!slots[i]) {
                            slots[i] = weapon;
                            break;
                        }
                    }
                }
            }
        }

        // Update visual elements
        this._updateWeaponMeshes();
        this._updateIconHighlights();
        this._updateAimAssistVisibility();
    }

    _onPlayButtonHit(buttonObj) {
        // Handle PlayButton being shot
        // For now, just log. You can trigger your game start logic here.
        // Call startGame on lobby-handler if present on parent
        if (buttonObj && buttonObj.parent) {
            const lobbyHandler = buttonObj.parent.getComponent && buttonObj.parent.getComponent('lobby-handler');
            if (lobbyHandler && typeof lobbyHandler.startGame === 'function') {
                lobbyHandler.startGame();
            }
        }
    }

    _checkPlayButtonRaycast(originObj, targetObj) {
        if (!originObj || !targetObj) return;
        originObj.getPositionWorld(this.pos);
        targetObj.getPositionWorld(this.pos2);
        // Use pre-allocated direction vector instead of creating new one
        vec3.subtract(this._raycastDirection, this.pos2, this.pos);
        vec3.normalize(this._raycastDirection, this._raycastDirection);
        const hit = this.engine.physics.rayCast(this.pos, this._raycastDirection, 1 << 0, 100);
        if (hit.hitCount > 0) {
            for (let i = 0; i < hit.hitCount; i++) {
                const obj = hit.objects[i];
                if (obj.name === 'PlayButton') {
                    // Call startGame on lobby-handler if present on parent
                    if (obj.parent && obj.parent.getComponent) {
                        const lobbyHandler = obj.parent.getComponent('lobby-handler');
                        if (lobbyHandler && typeof lobbyHandler.startGame === 'function') {
                            lobbyHandler.startGame();
                        }
                    }
                    // Disable the PlayButton after it is hit
                    obj.active = false;
                    this._onPlayButtonHit(obj);
                    break;
                } else if (obj.name === 'BuyButton') {
                    this.shopBuyButton.onPress();
                    break;
                }
            }
        }
    }

    /**
     * Apply weapon sway for more realistic shooting feel
     * @param {Array} direction - Direction vector to modify
     * @param {string} weaponType - Type of weapon for different sway amounts
     */
    _applyWeaponSway(direction, weaponType) {
        const baseSway = this._swayMultipliers[weaponType] || 0.02;

        // Add some randomness to direction - simulates hand shake and weapon handling
        const swayX = (Math.random() - 0.5) * baseSway;
        const swayY = (Math.random() - 0.5) * baseSway;
        const swayZ = (Math.random() - 0.5) * baseSway;

        // Apply sway to direction vector
        direction[0] += swayX;
        direction[1] += swayY;
        direction[2] += swayZ;

        // Renormalize to maintain proper direction
        vec3.normalize(direction, direction);
    }

    /**
     * Enhanced weapon switching with better feedback
     * @param {boolean} forceSwitch - Force weapon switch
     * @param {boolean} isKeyboard - Whether triggered by keyboard
     */
    _enhancedWeaponSwitch(forceSwitch, isKeyboard = false) {
        // Add tactile feedback for weapon switching
        if (this.networkHandler) {
            this.networkHandler.triggerHapticFeedback(0.4, 80, 'both');
        }

        // Play weapon switch sound
        if (this.unlockSound) {
            this.unlockSound.play();
        }

        // Call original weapon switch logic
        this._handleWeaponSwitch(forceSwitch, isKeyboard);
    }
}